import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { mcGetItem, mcGetCloudItem, mcListCloudSessionDetails } from "../utils/mcCloudSync";
import { idbGetJson, idbListItems } from "../utils/mcStorageEngine";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import "./mcArchive.css";
import {
  loadArchivedSessionRecords,
  saveArchivedSessionRecords,
  loadSessionRecords,
  loadSessionDetailSnapshotForCompare,
  findSessionSeedById,
  loadEngineState,
  MC_SESSION_EVENT,
  resolveFlagConfig,
  restoreArchivedSessionRecord,
} from "../utils/mcEngineData";
import { filterRecordsForProfile, isAdminProfile, useCurrentMcProfile } from "../utils/profileAccess";
import { activeSections } from "../utils/mcSections";
import { resolveCostVarianceFlagState } from "../utils/mcFlagging";

const detailStorageKeyFor = (sessionId) => `mc-session-engine:${sessionId}`;
const finalSnapshotStorageKeyFor = (sessionId) => `mc-final-count-snapshot:${sessionId}`;
const countLedgerStorageKeyFor = (sessionId) => `mc-count-ledger:${sessionId}`;

function isImmutableFinalSnapshot(payload) {
  return Boolean(payload && payload.immutable === true && payload.snapshotType === "final-count" && Array.isArray(payload.lines));
}

// MC498 — Compare Intelligence is allowed to hydrate immutable final evidence only
// for the two sessions the operator actually selected. Keep a tiny in-memory cache
// plus an in-flight promise cache so React dev/StrictMode mounts and harmless
// rerenders cannot issue the same Firestore getDoc twice for one snapshot.
const compareFinalSnapshotMemory = new Map();
const compareFinalSnapshotInFlight = new Map();
const MAX_COMPARE_FINAL_SNAPSHOT_CACHE = 6;

function rememberCompareFinalSnapshot(key, payload) {
  if (!key || !isImmutableFinalSnapshot(payload)) return payload;
  if (compareFinalSnapshotMemory.has(key)) compareFinalSnapshotMemory.delete(key);
  compareFinalSnapshotMemory.set(key, payload);
  while (compareFinalSnapshotMemory.size > MAX_COMPARE_FINAL_SNAPSHOT_CACHE) {
    const oldestKey = compareFinalSnapshotMemory.keys().next().value;
    if (!oldestKey) break;
    compareFinalSnapshotMemory.delete(oldestKey);
  }
  return payload;
}

async function loadCompareFinalSnapshotOnce(session = {}) {
  const sessionId = String(session?.sessionId || "").trim();
  if (!sessionId) return null;
  const key = String(session?.finalSnapshotKey || finalSnapshotStorageKeyFor(sessionId)).trim();
  if (!key) return null;

  const memory = compareFinalSnapshotMemory.get(key);
  if (isImmutableFinalSnapshot(memory)) return memory;

  try {
    const local = parseArchivePayload(mcGetItem(key));
    if (isImmutableFinalSnapshot(local)) return rememberCompareFinalSnapshot(key, local);
  } catch (_) {}

  try {
    const indexed = await idbGetJson(key, null);
    const parsedIndexed = typeof indexed === "string" ? parseArchivePayload(indexed) : indexed;
    if (isImmutableFinalSnapshot(parsedIndexed)) return rememberCompareFinalSnapshot(key, parsedIndexed);
  } catch (_) {}

  if (compareFinalSnapshotInFlight.has(key)) {
    return compareFinalSnapshotInFlight.get(key);
  }

  const request = (async () => {
    try {
      const cloud = parseArchivePayload(await mcGetCloudItem(key));
      return isImmutableFinalSnapshot(cloud) ? rememberCompareFinalSnapshot(key, cloud) : null;
    } catch (_) {
      return null;
    } finally {
      compareFinalSnapshotInFlight.delete(key);
    }
  })();

  compareFinalSnapshotInFlight.set(key, request);
  return request;
}

function roundTo2(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/,/g, ".") || 0);
  return Math.round(n * 100) / 100;
}

function formatCurrency(value) {
  const amount = roundTo2(value || 0);
  const abs = Math.abs(amount);
  const body = abs.toLocaleString(undefined, {
    minimumFractionDigits: abs % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `-R${body}` : `R${body}`;
}


function calculateArchivedExposureFromLines(lines) {
  if (!Array.isArray(lines) || !lines.length) return 0;
  let total = 0;
  lines.forEach((line) => {
    const direct = line?.costVariance ?? line?.finalCostVariance ?? line?.varianceValue;
    if (direct !== undefined && direct !== null && direct !== "") {
      total += Number(direct) || 0;
      return;
    }

    const hasCounted = line?.counted !== "" && line?.counted !== undefined && line?.counted !== null;
    const hasWaste = line?.waste !== "" && line?.waste !== undefined && line?.waste !== null;
    const touched = hasCounted || hasWaste || Boolean(String(line?.note || "").trim());

    if (!touched) return;
    const counted = liveCountNumber(line?.counted);
    const waste = liveCountNumber(line?.waste);
    const soh = Number(line?.systemSoh || line?.systemSOH || line?.soh || 0);
    const unitCost = Number(line?.cost || line?.retail_price || line?.price || 0);
    total += (counted + waste - soh) * unitCost;
  });
  return roundTo2(total || 0) || 0;
}

function resolveArchivedRecord(record, finalSnapshot = null) {
  if (!record?.sessionId) return record;
  try {
    // P5.3 — finalized history is projected from the immutable final snapshot.
    // The old session-engine/detailSnapshot sources are fallback-only for legacy
    // archives created before P5.2. Once an immutable snapshot exists, nothing
    // mutable may influence what Archives displays.
    const immutableSnapshot = isImmutableFinalSnapshot(finalSnapshot) ? finalSnapshot : null;

    // MC496 — version-2 archive rows are already frozen summary projections built
    // from the finalization/archive snapshot. Do not reopen a 100–250 KB session
    // detail merely to render the Archives list. Full immutable evidence remains
    // on-demand for View / Compare / export and for one-time legacy migration.
    if (!immutableSnapshot && Number(record?.archiveIndexVersion || 0) >= 2 && record?.archiveSummaryTrusted === true) {
      const totalLines = Number(record.totalLines || record.finalTotalLines || 0);
      const completedLines = isCompletedArchiveRecord(record)
        ? totalLines
        : Number(record.completedLines || record.finalCompletedLines || 0);
      const exposure = roundTo2(record.finalExposure ?? record.costExposure ?? record.totalExposure ?? record.exposure ?? 0);
      return {
        ...record,
        startedBy: record.startedBy || record.createdBy || record.userName || "User",
        completedLines,
        totalLines,
        costExposure: exposure,
        finalExposure: exposure,
        flaggedCount: Number(record.flaggedCount || record.finalFlagCount || record.flaggedProducts || 0),
      };
    }

    const raw = immutableSnapshot ? null : mcGetItem(detailStorageKeyFor(record.sessionId));
    if (!raw && !immutableSnapshot) {
      const detailExposure = calculateArchivedExposureFromLines(record?.detailSnapshot?.lines || record?.lines);
      const recordExposure = record.finalExposure ?? record.costExposure;
      return {
        ...record,
        startedBy: record.startedBy || record.createdBy || record.userName || "User",
        costExposure: roundTo2(Number(recordExposure || 0) !== 0 ? recordExposure : detailExposure),
        finalExposure: roundTo2(Number(recordExposure || 0) !== 0 ? recordExposure : detailExposure),
      };
    }
    const parsed = immutableSnapshot || JSON.parse(raw);
    const meta = parsed?.meta || {};
    const startedBy = meta.startedBy || record.startedBy || record.createdBy || record.userName || "User";
    const lastEditedBy = meta.lastEditedBy || record.lastEditedBy || startedBy;

    // IMPORTANT: finalized archive cards must display the frozen exposure snapshot
    // saved when Finalize Count was pressed. Do not recalculate here, because the
    // detail page is the source of truth for variance/exposure and archive rows can
    // otherwise drift after restore/finalize cycles.
    const snapshotSummary = immutableSnapshot?.meta?.sessionSummary || immutableSnapshot?.meta || {};
    const rawFrozenExposure = immutableSnapshot
      ? (snapshotSummary.costExposure ?? snapshotSummary.exposure ?? snapshotSummary.totalExposure)
      : (record.finalExposure ?? record.costExposure);
    const detailExposure = calculateArchivedExposureFromLines(parsed?.lines);
    const frozenExposure = rawFrozenExposure !== undefined && rawFrozenExposure !== null && rawFrozenExposure !== ""
      ? Number(rawFrozenExposure || 0)
      : detailExposure;
    const frozenCompleted = immutableSnapshot
      ? (snapshotSummary.completedLines ?? snapshotSummary.countedItems)
      : (record.finalCompletedLines ?? record.completedLines);
    const frozenFlags = immutableSnapshot
      ? (snapshotSummary.flaggedCount ?? snapshotSummary.flaggedItems ?? snapshotSummary.flaggedProducts)
      : (record.finalFlagCount ?? record.flaggedCount);
    const frozenTotal = immutableSnapshot
      ? (snapshotSummary.totalLines ?? immutableSnapshot?.lines?.length)
      : (record.finalTotalLines ?? record.totalLines);
    const detailTotal = Array.isArray(parsed?.lines) ? parsed.lines.length : 0;
    const isCompletedArchive = record.archiveType === "completed"
      || /finalized\s+completed|completed/i.test(String(record.archiveReason || ""));

    // Completed archive rows must display as fully counted. Older archive summary
    // records can carry stale finalCompletedLines values after a finalize/view cycle,
    // while the archived detail snapshot correctly opens as 90/90. Use the detail
    // snapshot total as the display source for completed archives so the archive
    // list and View Session page stay aligned.
    const displayTotal = Number(detailTotal || frozenTotal || 0);
    const displayCompleted = isCompletedArchive
      ? displayTotal
      : Number(frozenCompleted || 0);

    return {
      ...record,
      ...(immutableSnapshot ? {
        finalSnapshotKey: record.finalSnapshotKey || finalSnapshotStorageKeyFor(record.sessionId),
        finalSnapshotCapturedAt: immutableSnapshot.capturedAt || record.finalSnapshotCapturedAt || "",
        finalSnapshotSourceRevision: Number(immutableSnapshot.sourceLedgerRevision || record.finalSnapshotSourceRevision || 0),
        finalSnapshotDigest: immutableSnapshot.sourceLedgerDigest || record.finalSnapshotDigest || "",
        historicalAuthority: "immutable-final-snapshot",
        detailSnapshot: immutableSnapshot,
      } : {}),
      archiveType: isCompletedArchive ? "completed" : record.archiveType,
      status: isCompletedArchive ? "completed" : record.status,
      startedBy,
      lastEditedBy,
      completedLines: displayCompleted,
      flaggedCount: Number(frozenFlags || 0),
      totalLines: displayTotal,
      costExposure: roundTo2(frozenExposure || 0),
      finalExposure: roundTo2(frozenExposure || 0),
    };
  } catch {
    return record;
  }
}

function isCompletedArchiveRecord(record) {
  if (!record) return false;
  const status = String(record.status || record.sessionStatus || record.meta?.status || "").toLowerCase();
  const archiveType = String(record.archiveType || record.type || "").toLowerCase();
  const archiveReason = String(record.archiveReason || record.reason || "").toLowerCase();

  const completed = Number(record.completedLines ?? record.finalCompletedLines ?? 0);
  const total = Number(record.totalLines ?? record.finalTotalLines ?? record.lines?.length ?? 0);

  return archiveType === "completed"
    || status === "completed"
    || status === "finalized"
    || archiveReason.includes("completed")
    || (total > 0 && completed >= total);
}

function isFinalizedArchiveRecord(record) {
  if (!record) return false;
  const meta = record?.meta || {};
  const lifecycle = String(record?.lifecycleState || meta?.lifecycleState || "").trim().toLowerCase();
  const status = String(record?.status || record?.sessionStatus || meta?.status || "").trim().toLowerCase();
  const reason = String(record?.archiveReason || record?.reason || "").trim().toLowerCase();

  // Finalized is the authority boundary. A manually archived/test/abandoned count
  // must never influence reporting KPIs simply because it lives in Archives.
  return Boolean(
    record?.finalizedAt
    || meta?.finalizedAt
    || lifecycle === "finalized"
    || status === "finalized"
    || reason.startsWith("finalized")
  );
}

function formatExposure(value) {
  const amount = Number(value || 0);
  const abs = Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: Math.abs(amount) % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
  return amount < 0 ? `-R${abs}` : `R${abs}`;
}

function liveCountNumber(rawValue) {
  const source = String(rawValue ?? "").trim();
  if (!source) return 0;
  const normalized = source.replace(/,/g, ".").replace(/\s+/g, "");
  if (!/^[0-9+\-*/().]+$/.test(normalized) || normalized.includes("**") || normalized.includes("//")) {
    const fallback = Number(source.replace(/,/g, "."));
    return Number.isFinite(fallback) ? roundTo2(fallback) : 0;
  }
  const safeExpression = normalized.replace(/[+\-*/.]+$/g, "");
  if (!safeExpression) return 0;
  try {
    const result = Function('"use strict"; return (' + safeExpression + ');')();
    return Number.isFinite(result) ? roundTo2(result) : 0;
  } catch {
    const fallback = Number(safeExpression);
    return Number.isFinite(fallback) ? roundTo2(fallback) : 0;
  }
}

function formatPercent(value) {
  const n = Number(value || 0);
  return `${Math.round(n)}%`;
}

function isMorpheusLightTheme() {
  if (typeof document === "undefined") return false;
  const html = document.documentElement;
  const body = document.body;
  const classText = `${html?.className || ""} ${body?.className || ""}`.toLowerCase();
  const dataTheme = `${html?.dataset?.theme || ""} ${body?.dataset?.theme || ""} ${html?.dataset?.mcTheme || ""} ${body?.dataset?.mcTheme || ""}`.toLowerCase();
  const themeText = `${classText} ${dataTheme}`;
  if (/mc-theme-light|mc-light-theme|mc-light|\bis-light\b|data-mc-theme.*light/.test(themeText)) return true;
  if (/mc-theme-dark|mc-dark-theme|mc-dark|\bis-dark\b|data-mc-theme.*dark/.test(themeText)) return false;
  if (/light/.test(themeText)) return true;
  if (/dark/.test(themeText)) return false;
  try {
    const saved = `${localStorage.getItem("mc-theme") || ""} ${localStorage.getItem("morpheus-theme") || ""} ${localStorage.getItem("matrixos-mc-theme") || ""}`.toLowerCase();
    if (/dark/.test(saved)) return false;
    if (/light/.test(saved)) return true;
  } catch (_) {}
  return false;
}

function parseArchivePayload(raw) {
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}

function looksLikeCountResultRow(row) {
  if (!row || typeof row !== "object") return false;
  const hasCountField = Object.prototype.hasOwnProperty.call(row, "counted")
    || Object.prototype.hasOwnProperty.call(row, "waste")
    || Object.prototype.hasOwnProperty.call(row, "note")
    || Object.prototype.hasOwnProperty.call(row, "notes");
  const hasStockField = Object.prototype.hasOwnProperty.call(row, "systemSoh")
    || Object.prototype.hasOwnProperty.call(row, "systemSOH")
    || Object.prototype.hasOwnProperty.call(row, "soh")
    || Object.prototype.hasOwnProperty.call(row, "realSoh");
  return hasCountField && hasStockField;
}

function normalizeCompareLines(payload, { allowLegacyProducts = false } = {}) {
  // Current count-result payloads use one of these row authorities.
  if (Array.isArray(payload?.lines)) return payload.lines;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.countLines)) return payload.countLines;
  if (Array.isArray(payload?.sessionLines)) return payload.sessionLines;

  // MC470 — some valid completed/live session payloads from earlier Morpheus builds
  // stored the actual counted rows under `products`. We must support those rows, but
  // only when the caller has already proved that the payload belongs to this exact
  // session (exact storage key or embedded matching sessionId), and only when the
  // row shape itself is unmistakably a count result. This prevents the old 3,286-row
  // active inventory snapshot from ever being mistaken for a completed count.
  if (allowLegacyProducts && Array.isArray(payload?.products) && payload.products.length) {
    const sample = payload.products.slice(0, Math.min(payload.products.length, 12));
    const countShaped = sample.filter(looksLikeCountResultRow).length;
    if (countShaped >= Math.max(1, Math.ceil(sample.length * 0.75))) return payload.products;
  }

  // MC474 — partial/count-group sessions from older builds can persist their
  // completed row payload under `preparedRows` instead of `lines`. Never accept a
  // generic inventory preparedRows array: identity must already be proven by the
  // caller and the rows themselves must contain count-result fields. `realSoh` is
  // accepted above because that is the stock field used by these scoped payloads.
  if (allowLegacyProducts && Array.isArray(payload?.preparedRows) && payload.preparedRows.length) {
    const sample = payload.preparedRows.slice(0, Math.min(payload.preparedRows.length, 12));
    const countShaped = sample.filter(looksLikeCountResultRow).length;
    if (countShaped >= Math.max(1, Math.ceil(sample.length * 0.75))) return payload.preparedRows;
  }
  return [];
}

function normalizeDurableComparePayload(candidate, sessionId = "", { allowUnstamped = false } = {}) {
  const cleanId = String(sessionId || "").trim();
  const queue = [{ value: candidate, depth: 0, inheritedId: "" }];
  const seen = new Set();
  const matches = [];

  while (queue.length) {
    const { value, depth, inheritedId } = queue.shift();
    if (!value || typeof value !== "object" || seen.has(value) || depth > 5) continue;
    seen.add(value);

    const meta = value?.meta && typeof value.meta === "object" ? value.meta : {};
    const ownId = String(meta.sessionId || value.sessionId || value.id || "").trim();
    const effectiveId = ownId || inheritedId;
    const identityProven = !cleanId || effectiveId === cleanId || (allowUnstamped && !effectiveId);
    const lines = normalizeCompareLines(value, { allowLegacyProducts: identityProven });
    if (lines.length) {
      // Exact-key reads are allowed to hydrate older payloads that predate embedded
      // sessionId stamps. Broad fallback scans are not: they must prove identity so
      // unrelated inventory/session payloads can never be attached to this count.
      const identityMatches = identityProven;
      if (identityMatches) {
        matches.push({
          ...value,
          lines,
          meta: { ...meta, sessionId: meta.sessionId || value.sessionId || effectiveId || cleanId },
        });
      }
    }

    ["payload", "snapshot", "detailSnapshot", "session", "detail", "data", "state", "completedSnapshot", "finalSnapshot", "ledger"].forEach((key) => {
      if (value[key] && typeof value[key] === "object") queue.push({ value: value[key], depth: depth + 1, inheritedId: effectiveId });
    });
  }

  const touchedCount = (payload) => (payload?.lines || []).filter((line) => line && (
    String(line.counted ?? "").trim() !== "" ||
    String(line.waste ?? "").trim() !== "" ||
    String(line.note ?? line.notes ?? "").trim() !== ""
  )).length;
  const terminalScore = (payload) => {
    const meta = payload?.meta || {};
    const status = String(meta.status || payload?.status || "").toLowerCase();
    return Number(Boolean(
      meta.completedAt || meta.sectionCompletedAt || meta.finalizedAt || payload?.completedAt ||
      ["completed", "complete", "section complete", "section-complete", "finalized"].includes(status)
    ));
  };

  return matches.sort((a, b) =>
    (terminalScore(b) - terminalScore(a)) ||
    (touchedCount(b) - touchedCount(a)) ||
    ((b?.lines?.length || 0) - (a?.lines?.length || 0))
  )[0] || null;
}

function comparePayloadScore(payload) {
  if (!payload || !Array.isArray(payload.lines) || !payload.lines.length) return -1;
  const touched = payload.lines.filter((line) => line && (
    String(line.counted ?? "").trim() !== "" ||
    String(line.waste ?? "").trim() !== "" ||
    String(line.note ?? line.notes ?? "").trim() !== ""
  )).length;
  const meta = payload.meta || {};
  const status = String(meta.status || payload.status || "").toLowerCase();
  const terminal = Number(Boolean(meta.completedAt || meta.sectionCompletedAt || meta.finalizedAt || payload.completedAt || ["completed", "complete", "section complete", "section-complete", "finalized"].includes(status)));
  return terminal * 1000000 + touched * 1000 + payload.lines.length;
}

function loadSessionDetailSnapshot(session, asyncPayload = null) {
  // Compare/history must use the immutable final snapshot when available.
  if (isImmutableFinalSnapshot(asyncPayload) && normalizeCompareLines(asyncPayload, { allowLegacyProducts: true }).length) {
    return { ...asyncPayload, lines: normalizeCompareLines(asyncPayload, { allowLegacyProducts: true }) };
  }
  const localFinal = parseArchivePayload(mcGetItem(finalSnapshotStorageKeyFor(session.sessionId)));
  if (isImmutableFinalSnapshot(localFinal) && normalizeCompareLines(localFinal, { allowLegacyProducts: true }).length) {
    return { ...localFinal, lines: normalizeCompareLines(localFinal, { allowLegacyProducts: true }) };
  }
  // Active/completed sessions are canonically owned by the Count Ledger. A session
  // can therefore have a stale or missing legacy session-engine payload while the
  // durable ledger already contains the completed rows. Compare must hydrate from
  // that authority before falling back to the legacy detail document.
  const localLedger = parseArchivePayload(mcGetItem(countLedgerStorageKeyFor(session.sessionId)));
  if (normalizeCompareLines(localLedger, { allowLegacyProducts: true }).length) return { ...localLedger, lines: normalizeCompareLines(localLedger, { allowLegacyProducts: true }) };
  const localPayload = parseArchivePayload(mcGetItem(detailStorageKeyFor(session.sessionId)));
  if (normalizeCompareLines(localPayload, { allowLegacyProducts: true }).length) return { ...localPayload, lines: normalizeCompareLines(localPayload, { allowLegacyProducts: true }) };
  if (normalizeCompareLines(asyncPayload, { allowLegacyProducts: true }).length) return { ...asyncPayload, lines: normalizeCompareLines(asyncPayload, { allowLegacyProducts: true }) };
  if (session?.detailSnapshot && normalizeCompareLines(session.detailSnapshot, { allowLegacyProducts: true }).length) return { ...session.detailSnapshot, lines: normalizeCompareLines(session.detailSnapshot, { allowLegacyProducts: true }) };
  if (normalizeCompareLines(session, { allowLegacyProducts: false }).length) return { meta: session?.meta || {}, lines: normalizeCompareLines(session, { allowLegacyProducts: false }) };

  // MC471 — an active session can still have a valid canonical terminal summary
  // (for example 88/88, flags and exposure) even when an older development-era
  // transition lost its row array. Compare must preserve that factual summary in
  // the header instead of rendering the session as 0 products. This does NOT make
  // missing rows comparable; it only stops the read model from erasing a known
  // completed-product total.
  const summaryMeta = asyncPayload?.meta && typeof asyncPayload.meta === "object"
    ? asyncPayload.meta
    : (session?.meta && typeof session.meta === "object" ? session.meta : {});
  return {
    ...(asyncPayload && typeof asyncPayload === "object" ? asyncPayload : {}),
    meta: {
      ...summaryMeta,
      sessionId: summaryMeta.sessionId || session.sessionId,
      store: summaryMeta.store || session.storeName,
      storeName: summaryMeta.storeName || session.storeName,
    },
    lines: [],
  };
}

function buildSessionCompareStats(session, asyncPayload = null) {
  const payload = loadSessionDetailSnapshot(session, asyncPayload);
  const rows = Array.isArray(payload?.lines) ? payload.lines : [];
  const flagConfig = resolveFlagConfig(session?.storeName || session?.store || payload?.meta?.store || "ALL");
  let totalExposure = 0;
  let positiveExposure = 0;
  let negativeExposure = 0;
  let flaggedCount = 0;
  let completedLines = 0;
  let cleanLines = 0;
  let zeroVarianceLines = 0;
  const categories = new Map();
  const flaggedRows = [];

  rows.forEach((line) => {
    const touched = line.counted !== "" || line.waste !== "" || String(line.note || "").trim();
    const countedValue = touched ? liveCountNumber(line.counted) : null;
    const wasteValue = touched ? liveCountNumber(line.waste) : 0;
    const systemSoh = Number(line.systemSoh ?? line.systemSOH ?? line.soh ?? line.realSoh ?? 0);
    const unitCost = Number(line.cost ?? line.effectiveCost ?? line.sourceCost ?? 0);
    const variance = touched ? roundTo2(Number(countedValue || 0) + Number(wasteValue || 0) - systemSoh) : null;
    const costVariance = touched ? roundTo2(Number(variance || 0) * unitCost) : null;
    const noteFlagged = Boolean(String(line.note || "").trim());
    const thresholdFlags = touched ? resolveCostVarianceFlagState(costVariance, flagConfig) : { negativeFlagged: false, positiveFlagged: false };
    const { negativeFlagged, positiveFlagged } = thresholdFlags;
    const flagged = noteFlagged || negativeFlagged || positiveFlagged;
    const category = String(line.category || "Uncategorised").trim() || "Uncategorised";
    const bucket = categories.get(category) || { category, lines: 0, completed: 0, flags: 0, exposure: 0 };
    bucket.lines += 1;
    if (touched) {
      completedLines += 1;
      bucket.completed += 1;
      totalExposure += Number(costVariance || 0);
      if (Number(costVariance || 0) > 0) positiveExposure += Number(costVariance || 0);
      if (Number(costVariance || 0) < 0) negativeExposure += Number(costVariance || 0);
      if (Number(variance || 0) === 0) zeroVarianceLines += 1;
    }
    if (flagged) {
      flaggedCount += 1;
      bucket.flags += 1;
      flaggedRows.push({
        product: line.product || "Unnamed product",
        sku: line.sku || "—",
        category,
        variance,
        costVariance,
      });
    } else if (touched) {
      cleanLines += 1;
    }
    bucket.exposure += Number(costVariance || 0);
    categories.set(category, bucket);
  });

  // MC472 — Compare must never let a stale/empty recovery envelope erase the
  // factual terminal summary already carried by Sessions. Some legacy/data-loss
  // ledgers intentionally contain `0` summary fields while the active session
  // projection still correctly records 88/88, 93/93, 101/101, etc. `??` treated
  // that explicit zero as authoritative and hid the known total. Keep row arrays
  // authoritative for comparison math, but use the strongest positive terminal
  // summary for display/fallback metrics.
  const strongestPositiveNumber = (...values) => {
    const numbers = values
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    return numbers.length ? Math.max(...numbers) : 0;
  };
  const historicalTotalLines = strongestPositiveNumber(
    payload?.meta?.sessionSummary?.totalLines,
    payload?.meta?.totalLines,
    payload?.meta?.historicalTotalLines,
    session?.sessionSummary?.totalLines,
    session?.meta?.sessionSummary?.totalLines,
    session?.historicalTotalLines,
    session?.totalLines
  );
  const historicalCompletedLines = strongestPositiveNumber(
    payload?.meta?.sessionSummary?.completedLines,
    payload?.meta?.completedLines,
    payload?.meta?.historicalCompletedLines,
    session?.sessionSummary?.completedLines,
    session?.meta?.sessionSummary?.completedLines,
    session?.historicalCompletedLines,
    session?.completedLines
  );
  const totalLines = rows.length || historicalTotalLines;

  // MC476 — a completed scoped/partial count can retain its exact prepared scope
  // even when an older completion transition no longer exposes the counted row
  // payload to Compare. The session seed is safe for PRODUCT IDENTITY only: it is
  // the exact set that was prepared for this session, but its counted/waste values
  // are intentionally not used for movement maths. This lets Compare establish the
  // correct intersection (for example 88 of 101 products) without inventing any
  // historical variance values.
  let scopeRows = rows;
  if (!scopeRows.length && historicalTotalLines > 0) {
    try {
      const seed = findSessionSeedById(session?.sessionId);
      const seedRows = Array.isArray(seed?.preparedRows) ? seed.preparedRows : [];
      if (seedRows.length) {
        // Only trust the seed when its scope agrees with the factual terminal total.
        // This prevents a later/global seed from being attached to an older count.
        if (!historicalTotalLines || seedRows.length === historicalTotalLines) scopeRows = seedRows;
      }
    } catch (_) {}

    // MC477 — legacy partial sessions can outlive their original session seed. Their
    // archive record still retains the selected Count Groups, so rebuild PRODUCT
    // IDENTITY read-only from the site's current prepared inventory. This is only
    // accepted when the reconstructed scope exactly equals the immutable historical
    // total (88, 93, etc.); otherwise Compare refuses to guess. Count values from
    // these rows are never used for movement maths. No Firestore read/write is added.
    if (!scopeRows.length) {
      try {
        const isFullCount = session?.fullSite === true
          || String(session?.countScope || session?.sessionType || "").toLowerCase().includes("full")
          || String(archiveSectionName(session) || "").trim().toLowerCase() === "full count";

        // A historical Full Count's product identity is the whole prepared inventory
        // for that site. When the current prepared set still has exactly the same
        // factual size (e.g. archived 101 vs current partial 7), it is safe to use
        // those rows for IDENTITY ONLY. Historical counted/waste/variance values are
        // still never invented from this reconstruction.
        if (isFullCount) {
          const engine = loadEngineState(session?.storeKey || session?.storeName || session?.store || "ALL");
          const prepared = Array.isArray(engine?.preparedRows) ? engine.preparedRows : [];
          if (prepared.length === historicalTotalLines) scopeRows = prepared;
        }

        const scopeNames = archiveCategoryScopeNames(session);
        if (!scopeRows.length && scopeNames.length) {
          const engine = loadEngineState(session?.storeKey || session?.storeName || session?.store || "ALL");
          const prepared = Array.isArray(engine?.preparedRows) ? engine.preparedRows : [];
          const groups = Array.isArray(engine?.config?.categoryGroups) ? engine.config.categoryGroups : [];
          const wantedGroups = new Set(scopeNames.map((name) => String(name || "").trim().toLowerCase()).filter(Boolean));
          const groupOutputs = new Set(
            groups
              .filter((group) => wantedGroups.has(String(group?.name || "").trim().toLowerCase()))
              .flatMap((group) => Array.isArray(group?.outputs) ? group.outputs : [])
              .map((output) => String(output || "").trim().toLowerCase())
              .filter(Boolean)
          );
          const wantedCategories = new Set(scopeNames.map((name) => String(name || "").trim().toLowerCase()).filter(Boolean));
          const rebuilt = prepared.filter((line) => {
            const category = String(line?.category || line?.normalizedCategory || line?.output || line?.countGroup || line?.group || "").trim().toLowerCase();
            return groupOutputs.size ? groupOutputs.has(category) : wantedCategories.has(category);
          });
          if (rebuilt.length === historicalTotalLines) scopeRows = rebuilt;
        }
      } catch (_) {}
    }
  }
  const rowDetailUnavailable = rows.length === 0 && historicalTotalLines > 0;
  return {
    ...session,
    meta: payload?.meta || {},
    rows,
    scopeRows,
    totalLines,
    rowDetailUnavailable,
    completedLines: completedLines || historicalCompletedLines,
    flaggedCount: rows.length ? flaggedCount : Number(session.flaggedCount || 0),
    cleanLines,
    zeroVarianceLines,
    totalExposure: roundTo2(totalExposure || session.finalExposure || session.costExposure || 0),
    positiveExposure: roundTo2(positiveExposure),
    negativeExposure: roundTo2(negativeExposure),
    completionRate: totalLines ? Math.round(((completedLines || historicalCompletedLines) / totalLines) * 100) : 0,
    flagRate: totalLines ? Math.round(((rows.length ? flaggedCount : Number(session.flaggedCount || 0)) / totalLines) * 100) : 0,
    accuracyRate: completedLines ? Math.round((zeroVarianceLines / completedLines) * 100) : 0,
    categories: Array.from(categories.values()).sort((a, b) => Math.abs(b.exposure) - Math.abs(a.exposure)),
    flaggedRows: flaggedRows.sort((a, b) => Math.abs(Number(b.costVariance || 0)) - Math.abs(Number(a.costVariance || 0))).slice(0, 8),
  };
}


function normalizeArchiveText(value, fallback = "") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function archiveTimestampValue(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "object") {
    const seconds = Number(value.seconds ?? value._seconds);
    const nanoseconds = Number(value.nanoseconds ?? value._nanoseconds ?? 0);
    if (Number.isFinite(seconds)) return new Date((seconds * 1000) + Math.floor(nanoseconds / 1000000));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function sessionCreatedDate(session) {
  const candidates = [
    session.createdAt,
    session.startedAt,
    session.meta?.createdAt,
    session.meta?.startedAt,
    session.detailSnapshot?.meta?.createdAt,
    session.detailSnapshot?.meta?.startedAt,
    session.archivedAt,
    session.completedAt,
    session.updatedAt,
    session.meta?.archivedAt,
  ];
  for (const candidate of candidates) {
    const date = archiveTimestampValue(candidate);
    if (date) return date;
  }
  return null;
}

function archiveDateKey(session) {
  // The archive belongs to the operational/business date of the count, not
  // the UTC calendar day produced by Date#toISOString(). This matters for
  // stores operating ahead of UTC (for example South Africa): a count created
  // shortly after midnight local time must not be grouped under yesterday.
  const explicit = String(
    session?.operationalDate
      || session?.businessDate
      || session?.countDate
      || session?.meta?.operationalDate
      || session?.detailSnapshot?.meta?.operationalDate
      || ""
  ).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(explicit)) return explicit;

  const date = sessionCreatedDate(session);
  if (!date) return "Unknown date";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function archiveDateLabel(key) {
  if (!key || key === "Unknown date") return "Unknown date";
  try {
    return new Date(`${key}T12:00:00`).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch (_) {
    return key;
  }
}

function archiveCycleId(session) {
  return normalizeArchiveText(
    session.cycleId || session.cycleKey || session.countCycleId || session.meta?.cycleId || session.detailSnapshot?.meta?.cycleId,
    "uncycled"
  );
}

function archiveCycleName(session) {
  return normalizeArchiveText(
    session.cycleName || session.countCycleName || session.meta?.cycleName || session.detailSnapshot?.meta?.cycleName,
    archiveCycleId(session) === "uncycled" ? "Unassigned Cycle" : "Cycle"
  );
}

function archiveSectionName(session) {
  const full = session.fullSite === true || session.isFullSite === true || session.fullCount === true || session.isFullCount === true
    || /full/i.test(String(session.sessionType || session.countScope || session.type || session.meta?.sessionType || ""));
  if (full) return "Full Count";
  const section = normalizeArchiveText(
    session.sectionName || session.ownerSectionName || session.countSectionName || session.meta?.sectionName || session.detailSnapshot?.meta?.sectionName,
    "Section"
  );
  return /^full count$/i.test(section) ? "Full Count" : section;
}


function archiveCategoryScopeNames(session = {}) {
  const detailMeta = session?.detailSnapshot?.meta || {};
  const sources = [
    session?.countGroupNames,
    session?.meta?.countGroupNames,
    detailMeta?.countGroupNames,
    session?.categoryNames,
    session?.meta?.categoryNames,
    detailMeta?.categoryNames,
  ];
  for (const source of sources) {
    if (Array.isArray(source)) {
      const names = source.map((item) => String(item || '').trim()).filter(Boolean);
      if (names.length) return names;
    }
  }
  return [];
}

function archiveCategoryScopeLabel(session = {}) {
  const detailMeta = session?.detailSnapshot?.meta || {};
  const names = archiveCategoryScopeNames(session);
  return names.length
    ? names.join(' • ')
    : (session?.categoryScopeLabel || session?.meta?.categoryScopeLabel || detailMeta?.categoryScopeLabel || 'All Count Groups');
}
function archiveCategoryScopeKey(session = {}) {
  const names = archiveCategoryScopeNames(session);
  return names.length ? names.map((item) => String(item).trim().toLowerCase()).sort().join('|') : 'all';
}

function archiveStatusLabel(session) {
  if (isFinalizedArchiveRecord(session)) return "Finalized";
  return "Archived";
}

function archiveHasCorrection(session = {}) {
  const detailMeta = session?.detailSnapshot?.meta || {};
  const versions = session?.correctionVersions || session?.meta?.correctionVersions || detailMeta?.correctionVersions;
  return Boolean(
    session?.correctedAt
    || session?.meta?.correctedAt
    || detailMeta?.correctedAt
    || Number(session?.correctionVersion || session?.meta?.correctionVersion || detailMeta?.correctionVersion || 0) > 0
    || (Array.isArray(versions) && versions.length > 0)
  );
}


function archivedExposureValue(session) {
  return isFinalizedArchiveRecord(session)
    ? Number(session.costExposure || session.finalExposure || 0)
    : 0;
}

function archivedFlagValue(session) {
  return Number(session.flaggedCount || session.finalFlagCount || 0);
}


function archiveUnresolvedCount(session) {
  const explicit = Number(
    session?.unresolvedCount
    ?? session?.unresolvedResponses
    ?? session?.openResponseCount
    ?? session?.pendingResponseCount
    ?? session?.meta?.unresolvedCount
    ?? 0
  );
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const status = String(session?.responseStatus || session?.resolutionStatus || session?.verificationStatus || "").toLowerCase();
  if (/resolved|verified|closed/.test(status)) return 0;
  if (/awaiting|pending|unresolved|open|partially/.test(status)) return 1;
  return 0;
}

function buildArchiveHierarchy(sessions) {
  const dates = new Map();

  sessions.forEach((session) => {
    const dateKey = archiveDateKey(session);
    const siteName = normalizeArchiveText(session.storeName || session.store || session.siteName || session.meta?.storeName || session.meta?.store, "Unknown Site");
    const siteKey = normalizeArchiveText(session.storeId || session.siteId || siteName, siteName);
    const cycleId = archiveCycleId(session);
    const cycleName = archiveCycleName(session);
    const statusKey = isFinalizedArchiveRecord(session) ? "finalized" : "archived";
    const exposure = archivedExposureValue(session);
    const aggregateExposure = statusKey === "finalized" ? exposure : 0;
    const flags = archivedFlagValue(session);

    if (!dates.has(dateKey)) {
      dates.set(dateKey, {
        dateKey,
        dateLabel: archiveDateLabel(dateKey),
        sites: new Map(),
        count: 0,
        finalizedCount: 0,
        archivedCount: 0,
        exposure: 0,
        flags: 0,
        unresolved: 0,
      });
    }

    const date = dates.get(dateKey);
    date.count += 1;
    date.exposure += aggregateExposure;
    date.flags += flags;
    const unresolved = archiveUnresolvedCount(session);
    date.unresolved += unresolved;
    if (statusKey === "finalized") date.finalizedCount += 1;
    else date.archivedCount += 1;

    if (!date.sites.has(siteKey)) {
      date.sites.set(siteKey, {
        siteKey,
        siteName,
        groups: new Map(),
        count: 0,
        finalizedCount: 0,
        archivedCount: 0,
        exposure: 0,
        flags: 0,
        unresolved: 0,
      });
    }

    const site = date.sites.get(siteKey);
    site.count += 1;
    site.exposure += aggregateExposure;
    site.flags += flags;
    site.unresolved += unresolved;
    if (statusKey === "finalized") site.finalizedCount += 1;
    else site.archivedCount += 1;

    if (!site.groups.has(statusKey)) {
      site.groups.set(statusKey, {
        statusKey,
        title: statusKey === "finalized" ? "Finalized" : "Archived",
        cycles: new Map(),
        count: 0,
        exposure: 0,
        flags: 0,
        unresolved: 0,
      });
    }

    const group = site.groups.get(statusKey);
    group.count += 1;
    group.exposure += aggregateExposure;
    group.flags += flags;
    group.unresolved += unresolved;

    if (!group.cycles.has(cycleId)) {
      group.cycles.set(cycleId, {
        cycleId,
        cycleName,
        cycleDescription: normalizeArchiveText(session.cycleDescription || session.meta?.cycleDescription || session.detailSnapshot?.meta?.cycleDescription, ""),
        sessions: [],
        exposure: 0,
        flags: 0,
        completed: 0,
        unresolved: 0,
      });
    }

    const cycle = group.cycles.get(cycleId);
    cycle.sessions.push(session);
    cycle.exposure += aggregateExposure;
    cycle.flags += flags;
    cycle.unresolved += unresolved;
    if (statusKey === "finalized") cycle.completed += 1;
  });

  return Array.from(dates.values())
    .sort((a, b) => String(b.dateKey).localeCompare(String(a.dateKey)))
    .map((date) => ({
      ...date,
      exposure: roundTo2(date.exposure || 0),
      sites: Array.from(date.sites.values())
        .sort((a, b) => a.siteName.localeCompare(b.siteName))
        .map((site) => ({
          ...site,
          exposure: roundTo2(site.exposure || 0),
          groups: ["finalized", "archived"]
            .map((key) => site.groups.get(key))
            .filter(Boolean)
            .map((group) => ({
              ...group,
              exposure: roundTo2(group.exposure || 0),
              cycles: Array.from(group.cycles.values()).map((cycle) => ({
                ...cycle,
                exposure: roundTo2(cycle.exposure || 0),
                sessions: cycle.sessions.sort((a, b) => (sessionCreatedDate(a)?.getTime() || 0) - (sessionCreatedDate(b)?.getTime() || 0)),
              })),
            })),
        })),
    }));
}

function makeArchiveKey(...parts) {
  return parts.map((part) => String(part ?? "").replace(/[^a-z0-9_-]+/gi, "-")).join("__");
}

const MC_ARCHIVE_OPEN_STATE_KEY = "mc-archive-open-state-v2";
const MC_ARCHIVE_SCROLL_KEY = "mc-archive-return-position-v3";

function saveArchiveReturnPosition(sessionId = "", context = {}) {
  try {
    const payload = {
      y: Math.max(0, window.scrollY || 0),
      sessionId: String(sessionId || ""),
      dateKey: String(context?.dateKey || ""),
      siteKey: String(context?.siteKey || ""),
      groupKey: String(context?.groupKey || ""),
      openDates: context?.openDates && typeof context.openDates === "object" ? context.openDates : {},
      openSites: context?.openSites && typeof context.openSites === "object" ? context.openSites : {},
      openGroups: context?.openGroups && typeof context.openGroups === "object" ? context.openGroups : {},
      savedAt: Date.now(),
    };
    window.localStorage.setItem(MC_ARCHIVE_SCROLL_KEY, JSON.stringify(payload));
  } catch (_) {}
}

function consumeArchiveReturnPosition() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(MC_ARCHIVE_SCROLL_KEY) || "null");
    window.localStorage.removeItem(MC_ARCHIVE_SCROLL_KEY);
    if (!parsed || typeof parsed !== "object") return null;
    if (Date.now() - Number(parsed.savedAt || 0) > 30 * 60 * 1000) return null;
    const y = Number(parsed.y);
    return {
      y: Number.isFinite(y) ? Math.max(0, y) : 0,
      sessionId: String(parsed.sessionId || ""),
      dateKey: String(parsed.dateKey || ""),
      siteKey: String(parsed.siteKey || ""),
      groupKey: String(parsed.groupKey || ""),
      openDates: parsed?.openDates && typeof parsed.openDates === "object" ? parsed.openDates : {},
      openSites: parsed?.openSites && typeof parsed.openSites === "object" ? parsed.openSites : {},
      openGroups: parsed?.openGroups && typeof parsed.openGroups === "object" ? parsed.openGroups : {},
    };
  } catch (_) {
    return null;
  }
}

function readArchiveOpenState() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(MC_ARCHIVE_OPEN_STATE_KEY) || "{}");
    return {
      dates: parsed?.dates && typeof parsed.dates === "object" ? parsed.dates : {},
      sites: parsed?.sites && typeof parsed.sites === "object" ? parsed.sites : {},
      groups: parsed?.groups && typeof parsed.groups === "object" ? parsed.groups : {},
    };
  } catch (_) {
    return { dates: {}, sites: {}, groups: {} };
  }
}

function writeArchiveOpenState(next) {
  try {
    window.localStorage.setItem(MC_ARCHIVE_OPEN_STATE_KEY, JSON.stringify(next));
  } catch (_) {}
}

function ArchiveV2({ hierarchy, canRestore, onRestore, selectedIds, onToggleSelected, isSessionCompatible, returnState = null }) {
  // Archive expansion is page-session only, except for the explicit
  // Archives -> View Count -> Back to Archives round trip. That return path
  // restores the exact date/site/group investigation context once. Refreshing
  // Archives or switching Morpheus tabs still starts collapsed.
  const [openDates, setOpenDates] = useState(() => ({ ...(returnState?.openDates || {}) }));
  const [openSites, setOpenSites] = useState(() => ({ ...(returnState?.openSites || {}) }));
  const [openGroups, setOpenGroups] = useState(() => ({ ...(returnState?.openGroups || {}) }));

  if (!hierarchy.length) {
    return <div className="mc-archive-empty mc-archive-empty--card">No archived sessions yet.</div>;
  }

  const persistOpenState = () => {
    // No persistence by design. Expansion survives normal re-renders while the
    // user stays on this page, but resets when the page is revisited.
  };

  const toggleDate = (key) => {
    setOpenDates((current) => {
      const next = { ...current, [key]: !current[key] };
      persistOpenState("dates", next);
      return next;
    });
  };

  const toggleSite = (key) => {
    setOpenSites((current) => {
      const next = { ...current, [key]: !current[key] };
      persistOpenState("sites", next);
      return next;
    });
  };

  const toggleGroup = (key) => {
    setOpenGroups((current) => {
      const next = { ...current, [key]: !current[key] };
      persistOpenState("groups", next);
      return next;
    });
  };

  return (
    <div className="mc-archive-v2 mc-archive-v2--collapsed">
      {hierarchy.map((date) => {
        const dateOpen = Boolean(openDates[date.dateKey]);
        return (
          <section className="mc-archive-dateBlock" key={date.dateKey} data-archive-date-key={date.dateKey}>
            <button className="mc-archive-accordionHead mc-archive-accordionHead--date" type="button" onClick={() => toggleDate(date.dateKey)}>
              <div>
                <span>Date</span>
                <h2>{date.dateLabel}</h2>
              </div>
              <div className="mc-archive-accordionStats">
                <strong>{date.sites.length} site{date.sites.length === 1 ? "" : "s"}</strong>
                <small>{date.count} archived sections • {formatExposure(date.exposure)} • {date.flags} flags</small>
                <div className="mc-archive-summaryBadges">
                  {date.unresolved ? <span className="is-unresolved">{date.unresolved} unresolved</span> : null}
                </div>
              </div>
              <i>{dateOpen ? "−" : "+"}</i>
            </button>

            {dateOpen ? (
              <div className="mc-archive-siteStack">
                {date.sites.map((site) => {
                  const siteKey = makeArchiveKey(date.dateKey, site.siteKey);
                  const siteOpen = Boolean(openSites[siteKey]);
                  return (
                    <section className="mc-archive-site" key={siteKey}>
                      <button className="mc-archive-accordionHead mc-archive-accordionHead--site" type="button" onClick={() => toggleSite(siteKey)}>
                        <div>
                          <span>Site</span>
                          <h3>{site.siteName}</h3>
                        </div>
                        <div className="mc-archive-accordionStats">
                          <strong>{site.count} archived section{site.count === 1 ? "" : "s"}</strong>
                          <small>{site.finalizedCount} finalized • {site.archivedCount} archived • {formatExposure(site.exposure)}</small>
                          <div className="mc-archive-summaryBadges">
                            {site.unresolved ? <span className="is-unresolved">{site.unresolved} unresolved</span> : null}
                          </div>
                        </div>
                        <i>{siteOpen ? "−" : "+"}</i>
                      </button>

                      {siteOpen ? (
                        <div className="mc-archive-statusStack">
                          {site.groups.map((group) => {
                            const groupKey = makeArchiveKey(date.dateKey, site.siteKey, group.statusKey);
                            const groupOpen = Boolean(openGroups[groupKey]);
                            return (
                              <section className={`mc-archive-statusGroup mc-archive-statusGroup--${group.statusKey}`} key={groupKey}>
                                <button className="mc-archive-accordionHead mc-archive-accordionHead--group" type="button" onClick={() => toggleGroup(groupKey)}>
                                  <div>
                                    <span>{group.statusKey === "finalized" ? "Authoritative for reporting" : "Historical only · not finalized"}</span>
                                    <h4>{group.title}</h4>
                                  </div>
                                  <div className="mc-archive-accordionStats">
                                    <strong>{group.count} section{group.count === 1 ? "" : "s"}</strong>
                                    <small>{group.cycles.length} cycle{group.cycles.length === 1 ? "" : "s"} • {formatExposure(group.exposure)} • {group.flags} flags</small>
                                    <div className="mc-archive-summaryBadges">
                                      {group.unresolved ? <span className="is-unresolved">{group.unresolved} unresolved</span> : null}
                                    </div>
                                  </div>
                                  <i>{groupOpen ? "−" : "+"}</i>
                                </button>

                                {groupOpen ? (
                                  <div className="mc-archive-dateStack">
                                    {group.cycles.map((cycle) => (
                                      <article className="mc-archive-cycle" key={`${groupKey}-${cycle.cycleId}`}>
                                        <div className="mc-archive-cycleHead">
                                          <div>
                                            <h4>{cycle.cycleName}</h4>
                                            {cycle.cycleDescription ? <p>{cycle.cycleDescription}</p> : null}
                                          </div>
                                          <div className="mc-archive-cycleStats">
                                            <div className="mc-archive-cycleStatsLine">
                                              <strong>{group.statusKey === "finalized" ? `${cycle.sessions.length} finalized` : `${cycle.sessions.length} archived`}</strong>
                                              <span>{formatExposure(cycle.exposure)} • {cycle.flags} flags</span>
                                            </div>
                                            <div className="mc-archive-summaryBadges mc-archive-summaryBadges--cycle">
                                              {cycle.unresolved ? <span className="is-unresolved">{cycle.unresolved} unresolved</span> : null}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="mc-archive-cycleTable" role="table">
                                          <div className="mc-archive-cycleRow mc-archive-cycleRow--head" role="row">
                                            <span>Section</span>
                                            <span>Status</span>
                                            <span>Progress / exposure</span>
                                            <span>Action</span>
                                          </div>
                                          {cycle.sessions.map((session) => {
                                            const progress = `${Number(session.completedLines || 0)} / ${Number(session.totalLines || 0)}`;
                                            const exposure = archivedExposureValue(session);
                                            return (
                                              <div className={`mc-archive-cycleRow ${selectedIds?.has(session.sessionId) ? "is-selected" : ""}`} role="row" key={session.sessionId} data-archive-session-id={session.sessionId}>
                                                <div>
                                                  <strong>{archiveSectionName(session)}</strong>
                                                  <small>{session.sessionId}</small>
                                  <small className="mc-archive-category-scope">{archiveCategoryScopeLabel(session)} · {session.totalLines || session.detailSnapshot?.lines?.length || 0} products</small>
                                                </div>
                                                <div className="mc-archive-statusStackInline">
                                                  <span className={`mc-archive-status ${isFinalizedArchiveRecord(session) ? "is-finalized" : "is-archived"}`}>{archiveStatusLabel(session)}</span>
                                                  {archiveHasCorrection(session) ? <span className="mc-archive-rowBadge is-correction">Correction</span> : null}
                                                </div>
                                                <div className="mc-archive-progressExposure">
                                                  <span>{progress}</span>
                                                  <strong className={`mc-archive-exposure ${exposure < 0 ? "is-negative" : exposure > 0 ? "is-positive" : ""}`}>{formatExposure(exposure)}</strong>
                                                </div>
                                                <div className="mc-archive-rowActions">
                                                  <label
                                                    className={`mc-archive-compareCheck ${!selectedIds?.has(session.sessionId) && !isSessionCompatible?.(session) ? "is-disabled" : ""}`}
                                                    title={!selectedIds?.has(session.sessionId) && !isSessionCompatible?.(session) ? "Only finalized counts from the same section and site can be compared" : "Select this finalized section for comparison"}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      checked={selectedIds?.has(session.sessionId) || false}
                                                      disabled={!selectedIds?.has(session.sessionId) && !isSessionCompatible?.(session)}
                                                      onChange={() => onToggleSelected?.(session.sessionId)}
                                                    />
                                                    <span>Compare</span>
                                                  </label>
                                                  <Link
                                                    className="mc-archive-btn mc-archive-btn--primary"
                                                    to={`/morpheus-count/session/${session.sessionId}?archived=1`}
                                                    onClick={() => saveArchiveReturnPosition(session.sessionId, {
                                                      dateKey: date.dateKey,
                                                      siteKey,
                                                      groupKey,
                                                      openDates,
                                                      openSites,
                                                      openGroups,
                                                    })}
                                                  >View Count</Link>
                                                  {canRestore ? <button className="mc-archive-btn" type="button" onClick={() => onRestore(session)}>Restore</button> : null}
                                                  <div className="mc-archive-rowMeta">
                                                    <strong>{session.startedBy || session.createdBy || "User"}</strong>
                                                    <small>Archived {session.archivedAt ? new Date(session.archivedAt).toLocaleString() : "time not captured"}</small>
                                                    {archiveUnresolvedCount(session) ? <span className="mc-archive-rowBadge is-unresolved">Unresolved</span> : null}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </article>
                                    ))}
                                  </div>
                                ) : null}
                              </section>
                            );
                          })}
                        </div>
                      ) : null}
                    </section>
                  );
                })}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function CompareMetricCard({ label, value, sub, tone = "", trend }) {
  const lower = String(label || "").toLowerCase();
  const icon = trend === "up" ? "↗"
    : trend === "down" ? "↘"
      : lower.includes("flagged") ? "⚑"
        : lower.includes("resolved") ? "✓"
          : lower.includes("new") ? "+"
            : null;
  return (
    <article className={`mc-compare-metric ${tone ? `mc-compare-metric--${tone}` : ""} ${trend ? `mc-compare-metric--trend-${trend}` : ""}`}>
      <span>{label}</span>
      <strong>{icon ? <i className="mc-compare-trendIcon">{icon}</i> : null}{value}</strong>
      {sub ? <small>{sub}</small> : null}
    </article>
  );
}

function MiniBar({ value, max, tone = "cyan" }) {
  const pct = max ? Math.min(100, Math.max(4, Math.abs(Number(value || 0)) / max * 100)) : 0;
  return <span className={`mc-compare-miniBar mc-compare-miniBar--${tone}`}><i style={{ width: `${pct}%` }} /></span>;
}


function sessionTimeLabel(session) {
  const raw = session.archivedAt || session.completedAt || session.updatedAt || session.createdAt || session.meta?.archivedAt || session.meta?.createdAt;
  if (!raw) return "Time not captured";
  try { return new Date(raw).toLocaleString(); } catch { return String(raw); }
}

function sessionStartedByLabel(session) {
  return session?.startedBy || session?.createdBy || session?.userName || session?.meta?.startedBy || "User not captured";
}

function formatCountValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { minimumFractionDigits: Math.abs(n) % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
}

function productCompareKey(line) {
  const sku = String(line?.sku || "").trim().toLowerCase();
  if (sku) return `sku:${sku}`;
  return `name:${String(line?.product || "Unnamed product").trim().toLowerCase()}`;
}

function lineVarianceSnapshot(line, session) {
  const flagConfig = resolveFlagConfig(session?.storeName || session?.store || session?.meta?.store || "ALL");
  const touched = line.counted !== "" || line.waste !== "" || String(line.note || "").trim();
  const countedValue = touched ? liveCountNumber(line.counted) : null;
  const wasteValue = touched ? liveCountNumber(line.waste) : 0;
  const systemSoh = Number(line.systemSoh ?? line.systemSOH ?? line.soh ?? line.realSoh ?? 0);
  const unitCost = Number(line.cost ?? line.effectiveCost ?? line.sourceCost ?? 0);
  const variance = touched ? roundTo2(Number(countedValue || 0) + Number(wasteValue || 0) - systemSoh) : null;
  const costVariance = touched ? roundTo2(Number(variance || 0) * unitCost) : 0;
  const noteFlagged = Boolean(String(line.note || "").trim());
  const thresholdFlags = touched ? resolveCostVarianceFlagState(costVariance, flagConfig) : { negativeFlagged: false, positiveFlagged: false };
  const { negativeFlagged, positiveFlagged } = thresholdFlags;
  const flagged = noteFlagged || negativeFlagged || positiveFlagged;
  return {
    product: line.product || "Unnamed product",
    sku: line.sku || "—",
    category: String(line.category || "Uncategorised").trim() || "Uncategorised",
    counted: countedValue,
    waste: wasteValue,
    systemSoh,
    variance,
    costVariance,
    flagged,
    cost: Number(line.cost ?? line.effectiveCost ?? line.sourceCost ?? 0),
    note: line.note || "",
  };
}

function SessionComparisonPage({ sessions, onBack, backLabel = "Back to Archives" }) {
  const [productPage, setProductPage] = useState(1);
  const [asyncPayloads, setAsyncPayloads] = useState({});
  const [scopeModal, setScopeModal] = useState(null);
  const rowsPerPage = 7;
  // A primitive dependency keeps the hydration effect stable even when the parent
  // rebuilds the two-session array during harmless archive UI renders.
  const compareSessionsRef = useRef(sessions || []);
  compareSessionsRef.current = sessions || [];
  const compareHydrationKey = (sessions || []).map((session) => [
    String(session?.sessionId || "").trim(),
    String(session?.finalSnapshotKey || "").trim(),
    Number(session?.archiveIndexVersion || 0),
    session?.archiveSummaryTrusted === true ? 1 : 0,
  ].join(":")).join("|");

  useLayoutEffect(() => {
    if (typeof window !== "undefined") {
      window.history.scrollRestoration = "manual";
      window.scrollTo(0, 0);
      requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function hydrateMissingCompareDetails() {
      const next = {};
      const unresolved = [];

      const exactCandidateKeys = (sessionId) => [
        countLedgerStorageKeyFor(sessionId),
        `mc-session-completed:${sessionId}`,
        detailStorageKeyFor(sessionId),
        `mc-session-checkpoint:${sessionId}`,
        `mc-session-engine-${sessionId}`,
        `mc-session-detail:${sessionId}`,
      ];

      await Promise.all((compareSessionsRef.current || []).map(async (session) => {
        const sessionId = String(session?.sessionId || "").trim();
        if (!sessionId) return;

        // MC498 — trusted compact archive rows already prove which immutable final
        // snapshot owns the historical evidence. Compare therefore hydrates only
        // that one snapshot for this selected session and bypasses the old
        // ledger/checkpoint/session-detail compatibility sweep entirely.
        const trustedArchiveSummary = Number(session?.archiveIndexVersion || 0) >= 2
          && session?.archiveSummaryTrusted === true
          && isFinalizedArchiveRecord(session);
        if (trustedArchiveSummary) {
          const immutableFinal = await loadCompareFinalSnapshotOnce(session);
          if (immutableFinal?.lines?.length) {
            const normalizedFinal = normalizeDurableComparePayload(immutableFinal, sessionId, { allowUnstamped: true });
            next[sessionId] = normalizedFinal?.lines?.length ? normalizedFinal : immutableFinal;
          }
          // A trusted v2 archive with a missing immutable snapshot must fail closed:
          // keep the compact summary visible instead of launching an archive-wide
          // historical discovery scan that can recreate the old traffic spike.
          return;
        }

        const candidates = [];
        const summaryCandidates = [];
        const addCandidate = (value, allowUnstamped = false) => {
          try {
            const parsed = typeof value === "string" ? parseArchivePayload(value) : value;
            if (parsed && typeof parsed === "object") {
              const meta = parsed?.meta && typeof parsed.meta === "object" ? parsed.meta : {};
              const payloadId = String(meta.sessionId || parsed.sessionId || parsed.id || "").trim();
              const identityProven = payloadId === sessionId || (allowUnstamped && !payloadId);
              const summaryTotal = Number(meta?.sessionSummary?.totalLines ?? meta?.totalLines ?? meta?.historicalTotalLines ?? 0);
              if (identityProven && summaryTotal > 0) summaryCandidates.push(parsed);
            }
            const normalized = normalizeDurableComparePayload(parsed, sessionId, { allowUnstamped });
            if (normalized?.lines?.length) candidates.push(normalized);
          } catch (_) {}
        };

        const preferredFinalKey = session?.finalSnapshotKey || finalSnapshotStorageKeyFor(sessionId);
        try { addCandidate(mcGetItem(preferredFinalKey), true); } catch (_) {}
        try { addCandidate(await idbGetJson(preferredFinalKey, null), true); } catch (_) {}
        try { addCandidate(await mcGetCloudItem(preferredFinalKey), true); } catch (_) {}

        for (const key of exactCandidateKeys(sessionId)) {
          try { addCandidate(mcGetItem(key), true); } catch (_) {}
          try { addCandidate(await idbGetJson(key, null), true); } catch (_) {}
        }
        if (session?.detailSnapshot) addCandidate(session.detailSnapshot);
        addCandidate(session);

        if (!candidates.length) {
          for (const key of exactCandidateKeys(sessionId)) {
            try { addCandidate(await mcGetCloudItem(key), true); } catch (_) {}
          }
        }

        if (candidates.length) {
          candidates.sort((a, b) => comparePayloadScore(b) - comparePayloadScore(a));
          next[sessionId] = candidates[0];
        } else {
          // MC475 — Compare uses a read-only recovery authority. Partial sessions
          // are often represented by an original prepared shell plus a later
          // checkpoint/ledger containing the actual 88/88 (or similar) counted
          // rows. Search every durable mirror and choose the strongest touched
          // snapshot. Never call the repair helper here: Compare must not create
          // zero-row legacy ledgers or mutate verification/finalization state.
          try {
            const recovered = await loadSessionDetailSnapshotForCompare(sessionId, session);
            if (recovered?.lines?.length) {
              const normalized = normalizeDurableComparePayload(recovered, sessionId, { allowUnstamped: true });
              if (normalized?.lines?.length) candidates.push(normalized);
            }
          } catch (_) {}

          if (candidates.length) {
            candidates.sort((a, b) => comparePayloadScore(b) - comparePayloadScore(a));
            next[sessionId] = candidates[0];
          } else {
            // Preserve a trusted terminal summary even if row-level history is missing.
            // The broad row-discovery fallback still runs below and will replace this
            // with real rows if it finds them.
            if (summaryCandidates.length) {
              summaryCandidates.sort((a, b) => {
                const total = (x) => Number(x?.meta?.sessionSummary?.totalLines ?? x?.meta?.totalLines ?? x?.meta?.historicalTotalLines ?? 0);
                return total(b) - total(a);
              });
              next[sessionId] = summaryCandidates[0];
            }
            unresolved.push(sessionId);
          }
        }
      }));

      // MC468 — historical sessions can have their durable row payload under a
      // migration-era compatibility key even though Sessions still knows the correct
      // completed product total. Compare must discover those rows read-only rather
      // than converting the session to 0 products. One fallback scan serves every
      // unresolved selected session, so this only runs when narrow exact reads fail.
      if (unresolved.length) {
        const unresolvedSet = new Set(unresolved);
        const fallbackCandidates = new Map(unresolved.map((id) => [id, []]));
        const addFallback = (sessionId, value, allowUnstamped = false) => {
          if (!unresolvedSet.has(sessionId)) return;
          try {
            const parsed = typeof value === "string" ? parseArchivePayload(value) : value;
            const normalized = normalizeDurableComparePayload(parsed, sessionId, { allowUnstamped });
            if (normalized?.lines?.length) fallbackCandidates.get(sessionId).push(normalized);
          } catch (_) {}
        };

        try {
          const indexedRows = await idbListItems();
          (Array.isArray(indexedRows) ? indexedRows : []).forEach((item) => {
            const key = String(item?.key || "");
            unresolved.forEach((sessionId) => {
              if (key.includes(sessionId)) {
                // The storage key itself proves which session owns this legacy payload.
                addFallback(sessionId, item?.value, true);
                return;
              }
              try {
                const parsed = item?.value ? JSON.parse(item.value) : null;
                const normalized = normalizeDurableComparePayload(parsed, sessionId, { allowUnstamped: false });
                if (normalized?.meta?.sessionId === sessionId) fallbackCandidates.get(sessionId).push(normalized);
              } catch (_) {}
            });
          });
        } catch (_) {}

        try {
          const cloudDetails = await mcListCloudSessionDetails();
          (Array.isArray(cloudDetails) ? cloudDetails : []).forEach((item) => {
            const sessionId = String(item?.sessionId || "").trim();
            if (unresolvedSet.has(sessionId)) addFallback(sessionId, item?.value, true);
          });
        } catch (_) {}

        unresolved.forEach((sessionId) => {
          const candidates = fallbackCandidates.get(sessionId) || [];
          if (!candidates.length) return;
          candidates.sort((a, b) => comparePayloadScore(b) - comparePayloadScore(a));
          next[sessionId] = candidates[0];
        });
      }

      if (!cancelled && Object.keys(next).length) {
        setAsyncPayloads((current) => ({ ...current, ...next }));
      }
    }
    hydrateMissingCompareDetails();
    return () => { cancelled = true; };
  }, [compareHydrationKey]);

  const stats = useMemo(() => sessions.map((session) => buildSessionCompareStats(session, asyncPayloads[session?.sessionId])), [sessions, asyncPayloads]);
  const ordered = useMemo(() => {
    const compareTime = (item = {}) => {
      const raw = item?.sectionCompletedAt || item?.completedAt || item?.meta?.sectionCompletedAt || item?.meta?.completedAt || item?.startedAt || item?.createdAt || item?.archivedAt || 0;
      const time = new Date(raw || 0).getTime();
      return Number.isFinite(time) ? time : 0;
    };
    return [...stats].sort((a, b) => compareTime(a) - compareTime(b));
  }, [stats]);
  const firstSession = ordered[0];
  const lastSession = ordered[ordered.length - 1];

  const comparison = useMemo(() => {
    if (!firstSession || !lastSession) return {
      rows: [], improved: 0, worsened: 0, unchanged: 0, resolved: 0, newFlags: 0,
      comparedCount: 0, excludedFromA: 0, excludedFromB: 0,
      excludedA: [], excludedB: [], netImprovement: 0, netValueMovement: 0, aExposure: 0, bExposure: 0,
    };

    // MC476 — scope identity and variance detail are separate authorities. A partial
    // session whose historical row values are unavailable can still prove exactly
    // WHICH products belonged to that count from its session seed. Use that scope
    // for intersection/exclusion KPIs, while movement maths remains restricted to
    // real counted rows recovered from both sessions.
    const aScopeMap = new Map();
    const bScopeMap = new Map();
    (firstSession.scopeRows || firstSession.rows || []).forEach((line) => aScopeMap.set(productCompareKey(line), lineVarianceSnapshot(line, firstSession)));
    (lastSession.scopeRows || lastSession.rows || []).forEach((line) => bScopeMap.set(productCompareKey(line), lineVarianceSnapshot(line, lastSession)));

    const aDetailMap = new Map();
    const bDetailMap = new Map();
    (firstSession.rows || []).forEach((line) => aDetailMap.set(productCompareKey(line), lineVarianceSnapshot(line, firstSession)));
    (lastSession.rows || []).forEach((line) => bDetailMap.set(productCompareKey(line), lineVarianceSnapshot(line, lastSession)));

    // CI1: comparison authority is the product intersection. A partial recount must
    // never treat products omitted from either session as zero / resolved / new.
    const commonKeys = [...aScopeMap.keys()].filter((key) => bScopeMap.has(key));
    const excludedA = [...aScopeMap.keys()].filter((key) => !bScopeMap.has(key)).map((key) => aScopeMap.get(key));
    const excludedB = [...bScopeMap.keys()].filter((key) => !aScopeMap.has(key)).map((key) => bScopeMap.get(key));
    const rows = [];

    commonKeys.forEach((key) => {
      // Scope-only rows establish that the product belongs in the 88-vs-101
      // intersection, but they must never be converted into a fake zero variance.
      if (!aDetailMap.has(key) || !bDetailMap.has(key)) return;
      const a = aDetailMap.get(key);
      const b = bDetailMap.get(key);
      const aVariance = Number(a?.variance || 0);
      const bVariance = Number(b?.variance || 0);
      const unitCost = Math.abs(Number(b?.cost ?? a?.cost ?? 0));
      const varianceDifference = roundTo2(bVariance - aVariance);
      const aExposure = roundTo2(aVariance * unitCost);
      const bExposure = roundTo2(bVariance * unitCost);

      const exposureDifference = roundTo2(bExposure - aExposure);

      // MC191 — directional movement is intentionally business-neutral:
      // positive variance change = improved, negative variance change = worsened,
      // and zero = unchanged. Morpheus reports direction; each business decides
      // what that direction means operationally.
      const exposureImprovement = exposureDifference;

      const movement = varianceDifference > 0
        ? "improved"
        : varianceDifference < 0
          ? "worse"
          : "unchanged";

      // Keep the movement table focused on meaningful variance signals, but derive
      // them from every product actually counted in both sessions (not flag status).
      if (movement === "unchanged" && !a?.flagged && !b?.flagged) return;

      rows.push({
        key,
        product: b?.product || a?.product || "Unnamed product",
        sku: b?.sku || a?.sku || "—",
        category: b?.category || a?.category || "Uncategorised",
        a,
        b,
        aVariance,
        bVariance,
        varianceDifference,
        aExposure,
        bExposure,
        unitCost,
        exposureDifference,
        exposureImprovement,
        movement,
      });
    });

    const summary = rows.reduce((acc, row) => {
      if (row.movement === "improved") acc.improved += 1;
      if (row.movement === "worse") acc.worsened += 1;
      if (row.movement === "unchanged") acc.unchanged += 1;
      if (row.movement === "resolved") acc.resolved += 1;
      if (row.movement === "new") acc.newFlags += 1;
      acc.netImprovement += Number(row.exposureImprovement || 0);
      acc.netValueMovement += Number(row.exposureDifference || 0);
      acc.aExposure += Math.abs(Number(row.aExposure || 0));
      acc.bExposure += Math.abs(Number(row.bExposure || 0));
      return acc;
    }, { improved: 0, worsened: 0, unchanged: 0, resolved: 0, newFlags: 0, netImprovement: 0, netValueMovement: 0, aExposure: 0, bExposure: 0 });

    rows.sort((x, y) => Math.abs(Number(y.exposureImprovement || 0)) - Math.abs(Number(x.exposureImprovement || 0)) || Math.abs(Number(y.bExposure || 0)) - Math.abs(Number(x.bExposure || 0)));
    return {
      rows,
      ...summary,
      comparedCount: commonKeys.length,
      excludedFromA: excludedA.length,
      excludedFromB: excludedB.length,
      excludedA,
      excludedB,
      netImprovement: roundTo2(summary.netImprovement),
      aExposure: roundTo2(summary.aExposure),
      bExposure: roundTo2(summary.bExposure),
    };
  }, [firstSession, lastSession]);

  const outcomeSegments = [
    { key: "resolved", label: "Resolved", value: comparison.resolved, color: "#35d7ff", dot: "cyan" },
    { key: "improved", label: "Improved", value: comparison.improved, color: "#37e59a", dot: "green" },
    { key: "worse", label: "Worse", value: comparison.worsened, color: "#ff4d73", dot: "red" },
    { key: "new", label: "New", value: comparison.newFlags, color: "#f59e0b", dot: "amber" },
  ].filter((segment) => Number(segment.value || 0) > 0);
  const outcomeTotal = outcomeSegments.reduce((total, segment) => total + Number(segment.value || 0), 0);
  let outcomeStartDeg = 0;
  const outcomeGradient = outcomeSegments.length
    ? outcomeSegments.map((segment, index) => {
        const endDeg = index === outcomeSegments.length - 1
          ? 360
          : outcomeStartDeg + ((Number(segment.value || 0) / outcomeTotal) * 360);
        const piece = `${segment.color} ${outcomeStartDeg}deg ${endDeg}deg`;
        outcomeStartDeg = endDeg;
        return piece;
      }).join(", ")
    : "rgba(203,213,225,.72) 0deg 360deg";
  const comparedLabel = "2 selected sessions · A vs B";
  const outcomePct = (value) => outcomeTotal ? Math.round((Number(value || 0) / outcomeTotal) * 100) : 0;
  const pageCount = Math.max(1, Math.ceil(comparison.rows.length / rowsPerPage));
  const safeProductPage = Math.min(productPage, pageCount);
  const visibleRows = comparison.rows.slice((safeProductPage - 1) * rowsPerPage, safeProductPage * rowsPerPage);
  const topFlagged = [...comparison.rows]
    .filter((row) => row.movement !== "unchanged")
    .sort((a, b) => Math.abs(Number(b.bExposure || b.aExposure || b.exposureDifference || 0)) - Math.abs(Number(a.bExposure || a.aExposure || a.exposureDifference || 0)))
    .slice(0, 10);
  const topFlaggedMax = Math.max(1, ...topFlagged.map((row) => Math.abs(Number(row.bExposure || row.aExposure || row.exposureDifference || 0))));
  const notComparedCount = Number(comparison.excludedFromA || 0) + Number(comparison.excludedFromB || 0);
  const firstCycle = archiveCycleName(firstSession || {});
  const lastCycle = archiveCycleName(lastSession || {});
  const firstSection = archiveSectionName(firstSession || {});
  const lastSection = archiveSectionName(lastSession || {});
  const firstProductCount = Number(firstSession?.totalLines || firstSession?.rows?.length || 0);
  const lastProductCount = Number(lastSession?.totalLines || lastSession?.rows?.length || 0);
  const netMovementText = comparison.netValueMovement > 0
    ? `Net stock value increased by ${formatExposure(comparison.netValueMovement)}`
    : comparison.netValueMovement < 0
      ? `Net stock value decreased by ${formatExposure(Math.abs(comparison.netValueMovement))}`
      : "No net stock value movement";

  const openScopeModal = (side) => {
    if (side === "a") setScopeModal({ title: "Only in previous count", subtitle: `${comparison.excludedFromA} products were counted in A but not in B. They were excluded from comparison math.`, rows: comparison.excludedA || [] });
    else if (side === "b") setScopeModal({ title: "Only in newer count", subtitle: `${comparison.excludedFromB} products were counted in B but not in A. They were excluded from comparison math.`, rows: comparison.excludedB || [] });
    else setScopeModal({ title: "Products not compared", subtitle: `${notComparedCount} products existed in only one selected count. Morpheus did not treat them as zero, resolved, or new variance.`, rows: [
      ...(comparison.excludedA || []).map((row) => ({ ...row, scopeSide: "Previous only" })),
      ...(comparison.excludedB || []).map((row) => ({ ...row, scopeSide: "Newer only" })),
    ] });
  };

  const handleExportPdf = () => {
    try {
      const pdf = new jsPDF("l", "mm", "a4");
      const pageW = 297;
      const pageH = 210;
      const margin = 10;
      const light = isMorpheusLightTheme();
      const bg = light ? [239, 250, 255] : [3, 12, 24];
      const panel = light ? [255, 255, 255] : [8, 27, 50];
      const panel2 = light ? [247, 253, 255] : [5, 19, 38];
      const line = light ? [197, 225, 238] : [20, 75, 105];
      const text = light ? [7, 28, 45] : [235, 248, 255];
      const muted = light ? [58, 98, 122] : [145, 190, 225];
      const cyan = light ? [0, 120, 160] : [54, 216, 255];
      const green = light ? [0, 128, 87] : [70, 245, 175];
      const red = light ? [198, 16, 64] : [255, 98, 128];
      const amber = [245, 158, 11];
      const blue = [53, 215, 255];
      const grey = light ? [202, 215, 228] : [75, 95, 118];
      const rows = (comparison.rows || []).filter((row) => row.movement !== "unchanged");
      const exportedAt = new Date().toLocaleString();
      const siteName = lastSession?.storeName || lastSession?.store || firstSession?.storeName || firstSession?.store || "Selected sessions";
      const fileScope = String(siteName || "Comparison").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "Comparison";
      const fileBase = `${fileScope}-MC-Compare-${new Date().toISOString().slice(0, 10)}`;
      const clean = (value) => String(value ?? "");
      const money = (value) => formatExposure(value).replace("R", "R ");
      const rgb = (arr) => ({ r: arr[0], g: arr[1], b: arr[2] });
      const setText = (arr = text) => pdf.setTextColor(arr[0], arr[1], arr[2]);
      const setFill = (arr) => pdf.setFillColor(arr[0], arr[1], arr[2]);
      const setDraw = (arr = line) => pdf.setDrawColor(arr[0], arr[1], arr[2]);
      const rounded = (x, y, w, h, r = 4, fill = panel, stroke = line) => {
        setFill(fill); setDraw(stroke); pdf.roundedRect(x, y, w, h, r, r, "FD");
      };
      const addBackground = () => {
        setFill(bg); pdf.rect(0, 0, pageW, pageH, "F");
        setFill(light ? [218, 244, 251] : [4, 34, 60]); pdf.circle(250, 18, 44, "F");
        setFill(light ? [226, 247, 251] : [7, 32, 58]); pdf.circle(40, 38, 38, "F");
        setDraw(light ? [215, 235, 244] : [9, 48, 78]); pdf.setLineWidth(0.12);
        for (let x = 0; x <= pageW; x += 16) pdf.line(x, 0, x, pageH);
        for (let y = 0; y <= pageH; y += 16) pdf.line(0, y, pageW, y);
      };
      const footer = () => {
        setText(muted); pdf.setFontSize(7.5); pdf.setFont("helvetica", "normal");
        pdf.text(`MatrixOS Morpheus Count • Session Comparison • ${exportedAt}`, margin, pageH - 5);
        pdf.text(`Page ${pdf.internal.getNumberOfPages()}`, pageW - margin, pageH - 5, { align: "right" });
      };
      const metricCard = (x, y, w, label, value, sub, tone = "") => {
        rounded(x, y, w, 21, 4.5, panel, line);
        setText(cyan); pdf.setFont("helvetica", "bold"); pdf.setFontSize(6.7); pdf.text(clean(label).toUpperCase(), x + 4, y + 6);
        const color = tone === "positive" ? green : tone === "negative" ? red : tone === "warning" ? amber : tone === "resolved" ? blue : text;
        setText(color); pdf.setFontSize(13); pdf.text(clean(value), x + 4, y + 14.2);
        setText(muted); pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); pdf.text(clean(sub), x + 4, y + 18.5, { maxWidth: w - 8 });
      };
      const drawDonut = (cx, cy, radius, segments) => {
        const total = segments.reduce((sum, item) => sum + Number(item.value || 0), 0);
        if (!total) return;
        let start = -90;
        segments.forEach((item) => {
          const angle = (Number(item.value || 0) / total) * 360;
          const end = start + angle;
          const steps = Math.max(10, Math.ceil(angle / 6));
          const points = [[cx, cy]];
          for (let i = 0; i <= steps; i += 1) {
            const a = (start + (angle * i) / steps) * Math.PI / 180;
            points.push([cx + Math.cos(a) * radius, cy + Math.sin(a) * radius]);
          }
          setFill(item.color); pdf.triangle(points[0][0], points[0][1], points[1][0], points[1][1], points[2]?.[0] || points[1][0], points[2]?.[1] || points[1][1], "F");
          pdf.setFillColor(item.color[0], item.color[1], item.color[2]);
          for (let i = 1; i < points.length - 1; i += 1) pdf.triangle(cx, cy, points[i][0], points[i][1], points[i + 1][0], points[i + 1][1], "F");
          start = end;
        });
        setFill(panel); pdf.circle(cx, cy, radius * 0.56, "F");
        setText(text); pdf.setFont("helvetica", "bold"); pdf.setFontSize(15); pdf.text(String(outcomeTotal || rows.length), cx, cy - 1, { align: "center" });
        setText(muted); pdf.setFontSize(6); pdf.text("FLAGS", cx, cy + 5, { align: "center" });
      };

      addBackground();
      setText(cyan); pdf.setFont("helvetica", "bold"); pdf.setFontSize(7); pdf.text("FLAGGED ITEM COMPARISON", margin, 15);
      setText(text); pdf.setFontSize(24); pdf.setFont("helvetica", "normal"); pdf.text("Did the count improve?", margin, 27);
      setText(muted); pdf.setFontSize(8); pdf.text("Compare flagged variance from the older session (A) to the latest session (B). A is the older baseline; B is the newer result.", margin, 35);

      rounded(margin, 43, 86, 25, 5, panel, line);
      setText(cyan); pdf.setFont("helvetica", "bold"); pdf.setFontSize(6.5); pdf.text("A • OLDER SESSION", margin + 4, 49);
      setText(text); pdf.setFontSize(11); pdf.text(clean(firstSession?.storeName || firstSession?.store || "Session A"), margin + 4, 56);
      setText(muted); pdf.setFont("helvetica", "normal"); pdf.setFontSize(7); pdf.text(`${clean(firstSession?.sessionId)} • ${sessionTimeLabel(firstSession || {})}`, margin + 4, 62);
      pdf.text(`Started by ${sessionStartedByLabel(firstSession || {})}`, margin + 4, 66);

      rounded(103, 43, 86, 25, 5, panel, line);
      setText(cyan); pdf.setFont("helvetica", "bold"); pdf.setFontSize(6.5); pdf.text("B • NEWER SESSION", 107, 49);
      setText(text); pdf.setFontSize(11); pdf.text(clean(lastSession?.storeName || lastSession?.store || "Session B"), 107, 56);
      setText(muted); pdf.setFont("helvetica", "normal"); pdf.setFontSize(7); pdf.text(`${clean(lastSession?.sessionId)} • ${sessionTimeLabel(lastSession || {})}`, 107, 62);
      pdf.text(`Started by ${sessionStartedByLabel(lastSession || {})}`, 107, 66);

      const metricY = 74;
      metricCard(margin, metricY, 50, "Products compared", comparison.comparedCount, comparedLabel);
      metricCard(63, metricY, 34, "Resolved", comparison.resolved, "Reached zero in B", "resolved");
      metricCard(100, metricY, 43, "Improved", comparison.improved, "Variance improved from A to B", "positive");
      metricCard(146, metricY, 35, "Worse", comparison.worsened, "Variance moved further from zero", comparison.worsened ? "negative" : "positive");
      metricCard(184, metricY, 31, "New flags", comparison.newFlags, "Clean A, flagged B", comparison.newFlags ? "warning" : "positive");
      metricCard(218, metricY, 69, "Net stock value", `${comparison.netValueMovement > 0 ? "+" : ""}${money(comparison.netValueMovement)}`, "Signed stock value movement from A to B", comparison.netValueMovement > 0 ? "positive" : comparison.netValueMovement < 0 ? "negative" : "");

      rounded(margin, 102, 178, 60, 5, panel, line);
      setText(cyan); pdf.setFont("helvetica", "bold"); pdf.setFontSize(6.8); pdf.text("PRODUCT MOVEMENT", margin + 4, 109);
      setText(text); pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.text("Flagged variance and exposure movement", margin + 4, 116);
      autoTable(pdf, {
        startY: 121,
        margin: { left: margin + 4, right: 112 },
        head: [["Product", "A Var", "B Var", "Movement", "Cost Impact", "Status"]],
        body: rows.slice(0, 4).map((row) => [
          row.product,
          formatCountValue(row.aVariance),
          formatCountValue(row.bVariance),
          `${row.varianceDifference > 0 ? "+" : ""}${formatCountValue(row.varianceDifference)}`,
          money(row.exposureDifference),
          row.movement.toUpperCase(),
        ]),
        theme: "plain",
        styles: { font: "helvetica", fontSize: 7, cellPadding: { top: 2.2, right: 2, bottom: 2.2, left: 2 }, textColor: text, lineColor: line, lineWidth: 0.1 },
        headStyles: { textColor: cyan, fontStyle: "bold", fontSize: 6.2, halign: "left" },
        alternateRowStyles: { fillColor: panel2 },
        columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 17, halign: "center" }, 2: { cellWidth: 17, halign: "center" }, 3: { cellWidth: 20, halign: "center" }, 4: { cellWidth: 24, halign: "center" }, 5: { cellWidth: 22, halign: "center" } },
        didParseCell: (data) => {
          if (data.section === "body" && [3, 4].includes(data.column.index)) {
            const val = clean(data.cell.raw);
            if (val.startsWith("-")) data.cell.styles.textColor = red;
            if (val.startsWith("+") || (data.column.index === 4 && !val.startsWith("-") && val !== "R 0" && val !== "R 0.00")) data.cell.styles.textColor = green;
            data.cell.styles.fontStyle = "bold";
          }
          if (data.section === "body" && data.column.index === 5) {
            const val = clean(data.cell.raw).toLowerCase();
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = val.includes("worse") ? red : val.includes("new") ? amber : val.includes("resolved") ? blue : green;
          }
        },
      });

      rounded(195, 102, 92, 60, 5, panel, line);
      setText(cyan); pdf.setFont("helvetica", "bold"); pdf.setFontSize(6.8); pdf.text("OUTCOME SPLIT", 199, 109);
      setText(text); pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.text("Resolved vs improved vs worse vs new", 199, 116);
      const pdfSegments = outcomeSegments.map((s) => ({ ...s, color: s.key === "resolved" ? blue : s.key === "improved" ? green : s.key === "worse" ? red : amber }));
      drawDonut(218, 137, 17, pdfSegments);
      let ly = 128;
      pdfSegments.forEach((segment) => {
        setFill(segment.color); pdf.circle(245, ly - 1.2, 1.5, "F");
        setText(text); pdf.setFont("helvetica", "normal"); pdf.setFontSize(7.5); pdf.text(`${segment.label} ${segment.value} (${outcomePct(segment.value)}%)`, 249, ly);
        ly += 6.5;
      });
      footer();

      pdf.addPage();
      addBackground();
      setText(cyan); pdf.setFont("helvetica", "bold"); pdf.setFontSize(7); pdf.text("FULL FLAGGED MOVEMENT", margin, 15);
      setText(text); pdf.setFont("helvetica", "normal"); pdf.setFontSize(16); pdf.text("Comparable product movement", margin, 25);
      const pdfTopFlagged = topFlagged.slice(0, 5);
      rounded(195, 20, 92, 55, 5, panel, line);
      setText(cyan); pdf.setFont("helvetica", "bold"); pdf.setFontSize(7.2); pdf.text("TOP 5 FLAGGED", 199, 28);
      autoTable(pdf, {
        startY: 32,
        margin: { left: 199, right: 14 },
        head: [["Product", "Variance", "Exposure"]],
        body: pdfTopFlagged.map((row, index) => [
          `${index + 1}. ${row.product}`,
          formatCountValue(row.bVariance),
          money(row.bExposure || row.exposureDifference),
        ]),
        theme: "plain",
        styles: { font: "helvetica", fontSize: 7.2, cellPadding: { top: 1.6, right: 1, bottom: 1.6, left: 1 }, textColor: text, lineColor: line, lineWidth: 0.05 },
        headStyles: { textColor: cyan, fontStyle: "bold", fontSize: 6.4 },
        columnStyles: { 0: { cellWidth: 52 }, 1: { cellWidth: 15, halign: "right" }, 2: { cellWidth: 17, halign: "right" } },
        didParseCell: (data) => {
          if (data.section === "body" && [1, 2].includes(data.column.index)) {
            const val = clean(data.cell.raw);
            if (val.startsWith("-")) data.cell.styles.textColor = red;
            else if (val !== "0" && val !== "R 0" && val !== "R 0.00") data.cell.styles.textColor = green;
            data.cell.styles.fontStyle = "bold";
          }
        }
      });
      autoTable(pdf, {
        startY: 32,
        margin: { left: margin, right: 112 },
        head: [["Product", "A Var", "B Var", "Movement", "Cost Impact", "Status"]],
        body: rows.filter((row) => row.movement !== "unchanged").slice(4).map((row) => [
          row.product,
          formatCountValue(row.aVariance),
          formatCountValue(row.bVariance),
          `${row.varianceDifference > 0 ? "+" : ""}${formatCountValue(row.varianceDifference)}`,
          money(row.exposureDifference),
          row.movement.toUpperCase(),
        ]),
        theme: "grid",
        styles: { font: "helvetica", fontSize: 7, cellPadding: 2, textColor: text, fillColor: panel, lineColor: line, lineWidth: 0.1 },
        headStyles: { fillColor: light ? [225, 244, 250] : [8, 37, 66], textColor: cyan, fontStyle: "bold" },
        alternateRowStyles: { fillColor: panel2 },
        columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 18, halign: "right" }, 2: { cellWidth: 18, halign: "right" }, 3: { cellWidth: 22, halign: "right" }, 4: { cellWidth: 26, halign: "right" }, 5: { cellWidth: 24, halign: "center" } },
        didDrawPage: footer,
        didParseCell: (data) => {
          if (data.section === "body" && [3, 4].includes(data.column.index)) {
            const val = clean(data.cell.raw);
            if (val.startsWith("-")) data.cell.styles.textColor = red;
            if (val.startsWith("+") || (data.column.index === 4 && !val.startsWith("-") && val !== "R 0" && val !== "R 0.00")) data.cell.styles.textColor = green;
            data.cell.styles.fontStyle = "bold";
          }
          if (data.section === "body" && data.column.index === 5) {
            const val = clean(data.cell.raw).toLowerCase();
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = val.includes("worse") ? red : val.includes("new") ? amber : val.includes("resolved") ? blue : green;
          }
        },
      });

      let summaryTop = Math.max((pdf.lastAutoTable?.finalY || 32) + 9, 112);
      const summaryHeight = 98;
      const availableBottom = pageH - 12;
      if (summaryTop + summaryHeight > availableBottom) {
        footer();
        pdf.addPage();
        addBackground();
        summaryTop = 18;
      }
      setText(cyan); pdf.setFont("helvetica", "bold"); pdf.setFontSize(7); pdf.text("MANAGER SUMMARY", margin, summaryTop);
      setText(text); pdf.setFont("helvetica", "normal"); pdf.setFontSize(15); pdf.text("What this tells you", margin, summaryTop + 10);
      const biggestImprovement = comparison.rows.find((r) => r.exposureImprovement > 0)?.product || "—";
      const biggestConcern = comparison.rows.find((r) => r.exposureImprovement < 0)?.product || "—";
      const highestExposure = [...comparison.rows].sort((a,b)=>Math.abs(b.bExposure)-Math.abs(a.bExposure))[0]?.product || "—";
      const insight = (x, y, title, body, color = text, maxWidth = 72) => {
        setText(cyan); pdf.setFont("helvetica", "bold"); pdf.setFontSize(7); pdf.text(title.toUpperCase(), x + 5, y + 8);
        setText(color); pdf.setFont("helvetica", "bold"); pdf.setFontSize(9.2); pdf.text(body, x + 5, y + 18, { maxWidth });
      };
      rounded(margin, summaryTop + 17, 82, 28, 5, panel, line);
      rounded(107, summaryTop + 17, 82, 28, 5, panel, line);
      rounded(195, summaryTop + 17, 92, 28, 5, panel, line);
      insight(margin, summaryTop + 17, "Biggest improvement", biggestImprovement, green);
      insight(107, summaryTop + 17, "Biggest concern", biggestConcern, red);
      insight(195, summaryTop + 17, "Current highest exposure", highestExposure, text);
      rounded(margin, summaryTop + 52, 86, 26, 5, panel, line);
      rounded(102, summaryTop + 52, 86, 26, 5, panel, line);
      rounded(194, summaryTop + 52, 93, 26, 5, panel, line);
      insight(margin, summaryTop + 52, "A absolute exposure", money(comparison.aExposure), text);
      insight(102, summaryTop + 52, "B absolute exposure", money(comparison.bExposure), text);
      insight(194, summaryTop + 52, "Net stock value", `${comparison.netValueMovement > 0 ? "+" : ""}${money(comparison.netValueMovement)}`, comparison.netValueMovement >= 0 ? green : red);
      setText(muted); pdf.setFont("helvetica", "normal"); pdf.setFontSize(8);
      pdf.text("Exposure is calculated from comparable product variance multiplied by cost. Products counted in only one session are excluded from comparison math.", margin, summaryTop + 88, { maxWidth: 260 });
      footer();

      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileBase}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) {
      console.error("Compare sessions PDF export failed", error);
      alert("Compare sessions PDF export failed. Please try again.");
    }
  };

  return (
    <div className="mc-compare-page mc-compare-page--improvement mc-compare-page--ci2">
      <div className="mc-compare-header mc-compare-header--compact">
        <div>
          <div className="mc-archive-label">Count comparison intelligence</div>
          <h1>{firstSection === lastSection ? firstSection : `${firstSection} vs ${lastSection}`}</h1>
          <p className="mc-compare-oneLine">Same site + same section comparison. Morpheus compares only products counted in both sessions and keeps scope differences separate.</p>
        </div>
        <div className="mc-compare-headerActions mc-compare-headerActions--below">
          <button className="mc-archive-btn" type="button" onClick={handleExportPdf}>Export PDF</button>
          <button className="mc-archive-btn mc-archive-btn--primary" type="button" onClick={onBack}>{backLabel}</button>
        </div>
      </div>

      <div className="mc-compare-abGrid mc-compare-abGrid--ci2">
        <article className="mc-compare-sessionCard">
          <span>A · Previous count</span>
          <strong>{firstSession?.storeName || firstSession?.store || "Session A"}</strong>
          <div className="mc-compare-sessionMeta">
            <b>{firstCycle}</b><i>•</i><b>{firstSection}</b><i>•</i><b>{firstProductCount} products</b>
          </div>
          <small>{sessionTimeLabel(firstSession || {})}</small>
          <em>Counted by {sessionStartedByLabel(firstSession || {})}</em>
        </article>
        <article className="mc-compare-sessionCard mc-compare-sessionCard--b">
          <span>B · Newer count</span>
          <strong>{lastSession?.storeName || lastSession?.store || "Session B"}</strong>
          <div className="mc-compare-sessionMeta">
            <b>{lastCycle}</b><i>•</i><b>{lastSection}</b><i>•</i><b>{lastProductCount} products</b>
          </div>
          <small>{sessionTimeLabel(lastSession || {})}</small>
          <em>Counted by {sessionStartedByLabel(lastSession || {})}</em>
        </article>
      </div>

      {(firstSession?.rowDetailUnavailable || lastSession?.rowDetailUnavailable) ? (
        <div className="mc-compare-data-warning" role="status">
          <strong>Historical row detail unavailable</strong>
          <span>
            {firstSession?.rowDetailUnavailable && lastSession?.rowDetailUnavailable
              ? `Both selected sessions still record their completed product totals (${firstProductCount} and ${lastProductCount}), but their row-level count snapshots are no longer available. Morpheus will not invent comparison movement from summary totals.`
              : `${firstSession?.rowDetailUnavailable ? "The previous" : "The newer"} session still records ${firstSession?.rowDetailUnavailable ? firstProductCount : lastProductCount} completed products, but its row-level count snapshot is no longer available. Morpheus preserves that historical total and will not treat the missing rows as zero.`}
          </span>
        </div>
      ) : null}

      <section className="mc-compare-scopeStrip">
        <div className="mc-compare-scopeLead">
          <span>Comparison scope</span>
          <strong>{comparison.comparedCount} products compared</strong>
          <small>Intersection of products counted in both sessions</small>
        </div>
        <button type="button" onClick={() => openScopeModal("a")} disabled={!comparison.excludedFromA}>
          <span>Previous only</span><strong>{comparison.excludedFromA}</strong><small>Not counted in B</small>
        </button>
        <button type="button" onClick={() => openScopeModal("b")} disabled={!comparison.excludedFromB}>
          <span>Newer only</span><strong>{comparison.excludedFromB}</strong><small>Not counted in A</small>
        </button>
        <button type="button" className="mc-compare-scopeNotCompared" onClick={() => openScopeModal("all")} disabled={!notComparedCount}>
          <span>Not compared</span><strong>{notComparedCount}</strong><small>{notComparedCount ? "Click to view products" : "Scopes fully overlap"}</small>
        </button>
      </section>

      <div className="mc-compare-metrics mc-compare-metrics--ci2">
        <CompareMetricCard label="Resolved" value={comparison.resolved} sub="Variance reached zero in B" tone="resolved" />
        <CompareMetricCard label="Improved" value={comparison.improved} sub="Moved closer to zero" tone="positive" trend="up" />
        <CompareMetricCard label="Worsened" value={comparison.worsened} sub="Moved further from zero" tone={comparison.worsened ? "negative" : "positive"} trend="down" />
        <CompareMetricCard label="New variance" value={comparison.newFlags} sub="Zero in A, variance in B" tone={comparison.newFlags ? "warning" : "positive"} />
        <CompareMetricCard
          label="Net stock value movement"
          value={`${comparison.netValueMovement > 0 ? "+" : ""}${formatExposure(comparison.netValueMovement)}`}
          sub={netMovementText}
          tone={comparison.netValueMovement > 0 ? "positive" : comparison.netValueMovement < 0 ? "negative" : ""}
          trend={comparison.netValueMovement > 0 ? "up" : comparison.netValueMovement < 0 ? "down" : undefined}
        />
      </div>

      <div className="mc-compare-grid">
        <section className="mc-compare-panel mc-compare-panel--wide">
          <div className="mc-compare-panelHead">
            <div><span>Product movement</span><h3>Variance movement across comparable products</h3><p className="mc-compare-panelNote">Only meaningful variance signals are listed below. Unchanged clean products are still included in the “products compared” KPI.</p></div>
          </div>
          <div className="mc-compare-productTable">
            <div className="mc-compare-productHead"><span>Product</span><span>A variance</span><span>B variance</span><span>Change</span><span>Stock value change</span><span>Outcome</span></div>
            {comparison.rows.length ? visibleRows.map((row) => (
              <div className={`mc-compare-productRow is-${row.movement}`} key={row.key}>
                <div><strong>{row.product}</strong><small>SKU {row.sku} · {row.category}</small></div>
                <b>{formatCountValue(row.aVariance)}</b>
                <b>{formatCountValue(row.bVariance)}</b>
                <b className={row.varianceDifference > 0 ? "is-positive" : row.varianceDifference < 0 ? "is-negative" : ""}>{row.varianceDifference > 0 ? "+" : ""}{formatCountValue(row.varianceDifference)}</b>
                <b className={row.exposureDifference > 0 ? "is-positive" : row.exposureDifference < 0 ? "is-negative" : ""}>{row.exposureDifference > 0 ? "+" : ""}{formatExposure(row.exposureDifference)}</b>
                <span className={`mc-compare-status mc-compare-status--${row.movement}`}>{row.movement === "worse" ? "worsened" : row.movement === "new" ? "new variance" : row.movement}</span>
              </div>
            )) : <p className="mc-archive-empty">No meaningful variance movement found across the {comparison.comparedCount} comparable products.</p>}
          </div>
          {comparison.rows.length > rowsPerPage && (
            <div className="mc-compare-productPager">
              <button type="button" onClick={() => setProductPage((page) => Math.max(1, page - 1))} disabled={safeProductPage === 1}>‹</button>
              <span>Page {safeProductPage} of {pageCount}</span>
              <button type="button" onClick={() => setProductPage((page) => Math.min(pageCount, page + 1))} disabled={safeProductPage === pageCount}>›</button>
            </div>
          )}
        </section>

        <section className="mc-compare-panel">
          <div className="mc-compare-panelHead"><div><span>Outcome split</span><h3>How the comparable products moved</h3></div></div>
          <div className="mc-compare-outcomeBody">
            <div className="mc-compare-donut" style={{ background: `conic-gradient(${outcomeGradient})` }}>
              <div><strong>{outcomeTotal}</strong><span>signals</span></div>
            </div>
            <div className="mc-compare-legend">
              {outcomeSegments.length ? outcomeSegments.map((segment) => (
                <span key={segment.key}><i className={segment.dot} /> {segment.label} {segment.value} ({outcomePct(segment.value)}%)</span>
              )) : <span>No variance movement detected</span>}
            </div>
          </div>

          <div className="mc-compare-topFlags">
            <h4>Highest current exposure signals</h4>
            <div className="mc-compare-topFlagHead"><span>Product</span><span>Variance</span><span>Exposure</span></div>
            {topFlagged.length ? topFlagged.map((row, index) => {
              const variance = Number(row.bVariance || row.aVariance || row.varianceDifference || 0);
              const exposure = Number(row.bExposure || row.aExposure || row.exposureDifference || 0);
              const width = `${Math.max(8, Math.min(100, (Math.abs(exposure) / topFlaggedMax) * 100))}%`;
              return (
                <div className="mc-compare-topFlagRow" key={`top-${row.key}`}>
                  <span>{index + 1}</span>
                  <div className="mc-compare-topFlagProduct">
                    <p>{row.product}</p>
                    <i className="mc-compare-topFlagBar"><em className={exposure < 0 ? "is-negative-bg" : "is-positive-bg"} style={{ width }} /></i>
                  </div>
                  <b className={variance < 0 ? "is-negative" : variance > 0 ? "is-positive" : ""}>{formatCountValue(variance)}</b>
                  <b className={exposure < 0 ? "is-negative" : exposure > 0 ? "is-positive" : ""}>{formatExposure(exposure)}</b>
                </div>
              );
            }) : <p className="mc-archive-empty">No current variance signals found.</p>}
          </div>
        </section>
      </div>

      <div className="mc-compare-grid mc-compare-grid--bottom">
        <section className="mc-compare-panel">
          <div className="mc-compare-panelHead"><div><span>Exposure direction</span><h3>Previous risk vs newer risk</h3><p className="mc-compare-panelNote">Absolute exposure uses only products present in both selected counts. Lower absolute exposure means the count moved closer to zero.</p></div></div>
          <div className="mc-compare-abExposure">
            <div><span>A absolute exposure</span><strong>{formatExposure(comparison.aExposure)}</strong></div>
            <div><span>B absolute exposure</span><strong>{formatExposure(comparison.bExposure)}</strong></div>
            <div className={comparison.netImprovement >= 0 ? "is-positive" : "is-negative"}><span>Improvement</span><strong>{comparison.netImprovement >= 0 ? "+" : ""}{formatExposure(comparison.netImprovement)}</strong></div>
          </div>
        </section>

        <section className="mc-compare-panel mc-compare-insights">
          <div className="mc-compare-panelHead"><div><span>Manager summary</span><h3>What Morpheus sees</h3></div></div>
          <div className="mc-compare-insightGrid mc-compare-insightGrid--compact">
            <div><strong>Biggest improvement</strong><span>{comparison.rows.find((r) => r.exposureImprovement > 0)?.product || "—"}</span></div>
            <div><strong>Biggest concern</strong><span>{comparison.rows.find((r) => r.exposureImprovement < 0)?.product || "—"}</span></div>
            <div><strong>Current highest exposure</strong><span>{[...comparison.rows].sort((a,b)=>Math.abs(b.bExposure)-Math.abs(a.bExposure))[0]?.product || "—"}</span></div>
            <div><strong>Scope integrity</strong><span>{notComparedCount ? `${notComparedCount} products excluded because they were not counted in both sessions.` : "Both counts cover the same product scope."}</span></div>
          </div>
        </section>
      </div>

      {scopeModal ? (
        <div className="mc-compare-scopeModalBackdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setScopeModal(null); }}>
          <section className="mc-compare-scopeModal" role="dialog" aria-modal="true" aria-label={scopeModal.title}>
            <div className="mc-compare-scopeModalHead">
              <div><span>Scope detail</span><h3>{scopeModal.title}</h3><p>{scopeModal.subtitle}</p></div>
              <button type="button" onClick={() => setScopeModal(null)} aria-label="Close">×</button>
            </div>
            <div className="mc-compare-scopeModalList">
              {scopeModal.rows.length ? scopeModal.rows.map((row, index) => (
                <div className="mc-compare-scopeModalRow" key={`${productCompareKey(row)}-${index}`}>
                  <div><strong>{row.product || "Unnamed product"}</strong><small>SKU {row.sku || "—"} · {row.category || "Uncategorised"}</small></div>
                  {row.scopeSide ? <span>{row.scopeSide}</span> : null}
                </div>
              )) : <p className="mc-archive-empty">No excluded products.</p>}
            </div>
            <div className="mc-compare-scopeModalFoot"><button className="mc-archive-btn mc-archive-btn--primary" type="button" onClick={() => setScopeModal(null)}>Done</button></div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function ArchiveSection({ title, items, open, onToggle, canRestore, onRestore, selectedIds, onToggleSelected }) {
  return (
    <section className="mc-archive-section">
      <button type="button" className="mc-archive-sectionHead mc-archive-sectionHead--clickable" onClick={onToggle}>
        <h3>{title}</h3>
        <span>{items.length} {open ? "⌃" : "⌄"}</span>
      </button>

      {open ? (
        <div className="mc-archive-sectionBody">
          {!items.length ? (
            <div className="mc-archive-empty">Nothing archived here yet.</div>
          ) : items.map((session) => (
            <div className={`mc-archive-row ${selectedIds?.has(session.sessionId) ? "is-selected" : ""}`} key={`${title}-${session.sessionId}`}>
              <label className="mc-archive-compareCheck">
                <input type="checkbox" checked={selectedIds?.has(session.sessionId) || false} disabled={!selectedIds?.has(session.sessionId) && (selectedIds?.size || 0) >= 2} onChange={() => onToggleSelected?.(session.sessionId)} />
                <span>Compare</span>
              </label>
              <div>
                <div className="mc-archive-id">{session.sessionId}</div>
                <div className="mc-archive-store">{session.storeName}</div>
                <div className="mc-archive-meta">Started by {session.startedBy || "User"}</div>
                <div className="mc-archive-meta">
                  {session.completedLines || 0} / {session.totalLines || 0} counted • Archived {session.archivedAt ? new Date(session.archivedAt).toLocaleString() : ""}
                </div>
              </div>
              <div className="mc-archive-pill">{session.archiveReason || "Archived"}</div>
              <div className={`mc-archive-exposure ${Number(session.costExposure || 0) < 0 ? "is-negative" : Number(session.costExposure || 0) > 0 ? "is-positive" : ""}`}>
                {formatExposure(session.costExposure)}
              </div>
              <div className="mc-archive-actions">
                <Link className="mc-archive-btn mc-archive-btn--primary" to={`/morpheus-count/session/${session.sessionId}?archived=1`}>View Session</Link>
                {canRestore ? <button className="mc-archive-btn" type="button" onClick={() => onRestore(session)}>Restore</button> : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ConfirmModal({ label, title, body, confirmText, danger = false, busy = false, confirmDisabled = false, confirmDisabledText = "", onCancel, onConfirm, children, secondaryAction }) {
  return (
    <div className="mc-archive-confirmModal" role="dialog" aria-modal="true">
      <button
        className="mc-archive-confirmModal__backdrop"
        type="button"
        aria-label="Close restore modal"
        onClick={busy ? undefined : onCancel}
      />
      <div className={`mc-archive-confirmModal__card ${danger ? "is-danger" : ""}`}>
        <div className="mc-archive-label">{label}</div>
        <h3>{title}</h3>
        {body ? <p>{busy ? "Restoring this session to active sessions and updating the shared database…" : body}</p> : null}
        {children}
        <div className="mc-archive-confirmModal__actions">
          <button className="mc-archive-btn" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          {secondaryAction ? (
            <button className="mc-archive-btn mc-archive-btn--quiet" type="button" onClick={secondaryAction.onClick} disabled={busy}>
              {secondaryAction.label}
            </button>
          ) : null}
          {onConfirm ? (
            <div className="mc-archive-confirmModal__confirmWrap">
              <button className={`mc-archive-btn ${danger ? "mc-archive-btn--danger" : "mc-archive-btn--primary"}`} type="button" onClick={onConfirm} disabled={busy || confirmDisabled}>
                {busy ? "Restoring…" : (confirmDisabled && confirmDisabledText ? confirmDisabledText : confirmText)}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function MorpheusCountArchive() {
  const currentProfile = useCurrentMcProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState({ completed: false, incomplete: false, flagged: false });
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreReason, setRestoreReason] = useState("");
  const [restoreOtherReason, setRestoreOtherReason] = useState("");
  const [restoreConflict, setRestoreConflict] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [finalSnapshots, setFinalSnapshots] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const currentMonthKey = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const [showOlderArchives, setShowOlderArchives] = useState(false);
  const [archiveFilters, setArchiveFilters] = useState(() => ({ startDate: "", endDate: "", name: "", section: "" }));
  const [appliedArchiveFilters, setAppliedArchiveFilters] = useState(() => ({ startDate: "", endDate: "", name: "", section: "" }));
  const restoredArchiveScrollRef = useRef(false);
  const archiveCompareSelectRef = useRef(null);
  const archiveMigrationInFlightRef = useRef(false);
  const archiveMigrationAttemptedRef = useRef("");
  const [archiveReturnState] = useState(() => consumeArchiveReturnPosition());
  const canRestore = isAdminProfile(currentProfile);

  useEffect(() => {
    const refresh = () => setRefreshKey((v) => v + 1);
    // COST C3: Archives are historical/on-demand. No Firestore live listener.
    window.addEventListener("storage", refresh);
    window.addEventListener(MC_SESSION_EVENT, refresh);
    window.addEventListener("mc-cloud-sync", refresh);
    window.addEventListener("mc-cloud-sync-ready", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener(MC_SESSION_EVENT, refresh);
      window.removeEventListener("mc-cloud-sync", refresh);
      window.removeEventListener("mc-cloud-sync-ready", refresh);
    };
  }, []);

  const rawArchived = useMemo(() => {
    const activeIds = new Set(
      loadSessionRecords()
        .filter((item) => {
          const status = String(item?.status || "").toLowerCase();
          return item && item.archived !== true && status !== "archived" && !item.archivedAt;
        })
        .map((item) => String(item?.sessionId || "").trim())
        .filter(Boolean)
    );
    const records = loadArchivedSessionRecords()
      .filter((item) => item?.sessionId)
      .filter((item) => !activeIds.has(String(item.sessionId || "").trim()));
    return Array.from(new Map(records.map((item) => [item.sessionId, item])).values());
  }, [refreshKey]);

  useEffect(() => {
    let cancelled = false;
    async function hydrateImmutableArchiveAuthority() {
      // MC498 — Compare is an on-demand detail route. Never run the archive-wide
      // legacy migration/existence sweep behind it; only the two selected sessions
      // may hydrate full immutable evidence while comparison is open.
      if (new URLSearchParams(location.search).get("compare") === "1") return;

      // MC496 — only legacy archive rows need full immutable snapshots during list
      // hydration. Version-2 rows already carry the frozen summary projection, so
      // Archives becomes metadata-first and full evidence stays on-demand.
      const candidates = rawArchived.filter((record) => {
        if (!record?.sessionId) return false;
        if (Number(record?.archiveIndexVersion || 0) >= 2 && record?.archiveSummaryTrusted === true) return false;
        return Boolean(record.finalSnapshotKey || isCompletedArchiveRecord(record));
      });

      // Old manually archived/incomplete rows have no immutable final snapshot.
      // Their archive transition summary is the best available authority and can
      // be safely promoted into the compact v2 index immediately.
      const incompleteLegacyIds = new Set(
        rawArchived
          .filter((record) => record?.sessionId)
          .filter((record) => !(Number(record?.archiveIndexVersion || 0) >= 2 && record?.archiveSummaryTrusted === true))
          .filter((record) => !isCompletedArchiveRecord(record))
          .map((record) => String(record.sessionId || "").trim())
          .filter(Boolean)
      );

      if (!candidates.length && !incompleteLegacyIds.size) return;

      // MC497 — archive migration is a one-shot operation per mounted Archives page.
      // mc-session-archive writes emit session/cloud refresh events; before MC497 a
      // stale browser fallback could make those refreshes rebuild the same legacy
      // candidate list while the migration was still settling. That created an
      // accidental loop that re-read every immutable final snapshot over and over.
      // The compact index precedence fix in mcEngineData makes the v2 rows durable;
      // this guard also prevents overlapping/repeated migration passes in the same
      // mount if refresh events arrive during the Firestore write/hydration window.
      const migrationFingerprint = [
        ...candidates.map((record) => `final:${String(record?.sessionId || "").trim()}`),
        ...Array.from(incompleteLegacyIds).map((sessionId) => `incomplete:${sessionId}`),
      ].filter(Boolean).sort().join("|");
      if (!migrationFingerprint) return;
      if (archiveMigrationInFlightRef.current) return;
      if (archiveMigrationAttemptedRef.current === migrationFingerprint) return;
      archiveMigrationAttemptedRef.current = migrationFingerprint;
      archiveMigrationInFlightRef.current = true;

      const next = {};
      try {
        await Promise.all(candidates.map(async (record) => {
        const sessionId = String(record.sessionId || "").trim();
        const key = record.finalSnapshotKey || finalSnapshotStorageKeyFor(sessionId);
        const localFinal = parseArchivePayload(mcGetItem(key));
        if (isImmutableFinalSnapshot(localFinal)) {
          next[sessionId] = localFinal;
          return;
        }
        try {
          const cloudFinal = parseArchivePayload(await mcGetCloudItem(key));
          if (isImmutableFinalSnapshot(cloudFinal)) next[sessionId] = cloudFinal;
        } catch (err) {
          console.warn("MC archive immutable snapshot hydrate failed", sessionId, err);
        }
        }));

        if (cancelled) return;
        if (Object.keys(next).length) {
          setFinalSnapshots((current) => ({ ...current, ...next }));
        }

        // One-time migration: shrink the shared mc-session-archive document into a
        // compact summary index. Completed rows are only marked trusted when their
        // immutable final snapshot was actually recovered; failed snapshot reads stay
        // legacy so a later visit can retry. This write is bounded to the migration,
        // not every Archives visit.
        let promoted = false;
        const migrated = rawArchived.map((record) => {
          const sessionId = String(record?.sessionId || "").trim();
          if (!sessionId) return record;
          if (Number(record?.archiveIndexVersion || 0) >= 2 && record?.archiveSummaryTrusted === true) return record;
          const immutable = next[sessionId];
          if (immutable) {
            promoted = true;
            return {
              ...resolveArchivedRecord(record, immutable),
              archiveIndexVersion: 2,
              archiveSummaryTrusted: true,
            };
          }
          if (incompleteLegacyIds.has(sessionId)) {
            promoted = true;
            return {
              ...record,
              archiveIndexVersion: 2,
              archiveSummaryTrusted: true,
            };
          }
          return record;
        });

        if (promoted) saveArchivedSessionRecords(migrated);
      } finally {
        archiveMigrationInFlightRef.current = false;
      }
    }
    hydrateImmutableArchiveAuthority();
    return () => { cancelled = true; };
  }, [rawArchived, location.search]);

  const archived = useMemo(() => {
    return filterRecordsForProfile(rawArchived, currentProfile)
      .map((record) => resolveArchivedRecord(record, finalSnapshots[record.sessionId]));
  }, [rawArchived, currentProfile, finalSnapshots]);

  const groups = useMemo(() => {
    const finalized = archived.filter(isFinalizedArchiveRecord);
    const finalizedIds = new Set(finalized.map((item) => item.sessionId));
    return {
      finalized,
      archivedOnly: archived.filter((item) => !finalizedIds.has(item.sessionId)),
      flagged: archived.filter((item) => Number(item.flaggedCount || 0) > 0),
    };
  }, [archived]);

  const archiveUserOptions = useMemo(() => {
    const names = new Set();
    archived.forEach((session) => {
      [
        session.startedBy,
        session.createdBy,
        session.userName,
        session.lastEditedBy,
        session.archivedBy,
        session.meta?.startedBy,
        session.meta?.lastEditedBy,
        session.detailSnapshot?.meta?.startedBy,
        session.detailSnapshot?.meta?.lastEditedBy,
      ].forEach((name) => {
        const clean = String(name || "").trim();
        if (clean && !/^user not captured$/i.test(clean)) names.add(clean);
      });
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [archived]);

  const archiveSectionOptions = useMemo(() => {
    const map = new Map();
    activeSections().forEach((section) => {
      const name = String(section?.name || section?.sectionName || section?.label || "").trim();
      if (name) map.set(name.toLowerCase(), name);
    });
    archived.forEach((session) => {
      const name = archiveSectionName(session);
      if (name && name !== "Section") map.set(String(name).toLowerCase(), name);
    });
    map.set("full count", "Full Count");
    return Array.from(map.values()).sort((a, b) => {
      if (a === "Full Count") return 1;
      if (b === "Full Count") return -1;
      return a.localeCompare(b);
    });
  }, [archived]);

  const filteredArchived = useMemo(() => {
    const startDateQuery = String(appliedArchiveFilters.startDate || appliedArchiveFilters.date || "").trim();
    const endDateQuery = String(appliedArchiveFilters.endDate || "").trim();
    const nameQuery = String(appliedArchiveFilters.name || "").trim().toLowerCase();
    const sectionQuery = String(appliedArchiveFilters.section || "").trim().toLowerCase();

    const shouldLimitToCurrentMonth = !startDateQuery && !endDateQuery && !nameQuery && !sectionQuery && !showOlderArchives;

    return archived.filter((session) => {
      const dateKeyRaw = archiveDateKey(session);
      const dateKey = dateKeyRaw.toLowerCase();
      const dateLabel = archiveDateLabel(dateKeyRaw).toLowerCase();
      const nameBlob = [
        session.startedBy,
        session.createdBy,
        session.userName,
        session.lastEditedBy,
        session.archivedBy,
        session.meta?.startedBy,
        session.meta?.lastEditedBy,
        session.detailSnapshot?.meta?.startedBy,
        session.detailSnapshot?.meta?.lastEditedBy,
      ].filter(Boolean).join(" ").toLowerCase();
      const sectionBlob = [
        archiveSectionName(session),
        session.sectionName,
        session.ownerSectionName,
        session.countSectionName,
        session.cycleName,
        session.countCycleName,
        session.storeName,
        session.store,
        session.siteName,
      ].filter(Boolean).join(" ").toLowerCase();

      const monthMatches = !shouldLimitToCurrentMonth || dateKey.startsWith(currentMonthKey);
      const startMatches = !startDateQuery || dateKeyRaw >= startDateQuery;
      const endMatches = !endDateQuery || dateKeyRaw <= endDateQuery;
      const dateMatches = startMatches && endMatches;
      const nameMatches = !nameQuery || nameBlob.includes(nameQuery);
      const sectionMatches = !sectionQuery || sectionBlob.includes(sectionQuery);
      return monthMatches && dateMatches && nameMatches && sectionMatches;
    });
  }, [archived, appliedArchiveFilters, showOlderArchives, currentMonthKey]);

  const applyArchiveFilters = (event) => {
    event?.preventDefault?.();
    setAppliedArchiveFilters({ ...archiveFilters });
  };

  const clearArchiveFilters = () => {
    const clean = { startDate: "", endDate: "", name: "", section: "" };
    setArchiveFilters(clean);
    setAppliedArchiveFilters(clean);
    setShowOlderArchives(false);
  };

  const filtersActive = Boolean(appliedArchiveFilters.startDate || appliedArchiveFilters.endDate || appliedArchiveFilters.date || appliedArchiveFilters.name || appliedArchiveFilters.section);

  const compareParams = new URLSearchParams(location.search);
  const compareMode = compareParams.get("compare") === "1";
  const compareSource = String(compareParams.get("source") || "archive").toLowerCase();
  const compareBackToSessions = compareSource === "active" || compareSource === "sessions";
  const compareBackPath = compareBackToSessions ? "/morpheus-count/sessions" : "/morpheus-count/archive";
  const compareBackLabel = compareBackToSessions ? "Back to Sessions" : "Back to Archives";
  const compareIds = (compareParams.get("ids") || "").split(",").map((id) => id.trim()).filter(Boolean).slice(0, 2);
  const activeCompareSessions = useMemo(() => {
    if (compareIds.length < 2) return [];
    // MC472 — active Sessions must stay on the Sessions read model. Running an
    // active record through resolveArchivedRecord() made Compare read the legacy
    // mc-session-engine document and could overwrite a correct 88/88 (or 101/101)
    // Sessions summary with a stale 0-row archive-style projection. Row hydration
    // below still independently discovers the canonical ledger/final/checkpoint
    // payloads; this list only supplies identity + factual Sessions metadata.
    return filterRecordsForProfile(loadSessionRecords(), currentProfile)
      .filter((item) => compareIds.includes(item?.sessionId))
      .map((item) => ({ ...item }));
  }, [currentProfile, refreshKey, location.search]);
  const compareSessions = Array.from(
    new Map([...archived, ...activeCompareSessions].filter((item) => compareIds.includes(item.sessionId)).map((item) => [item.sessionId, item])).values()
  );


  const archivedById = useMemo(
    () => new Map(archived.map((session) => [String(session.sessionId || ""), session])),
    [archived]
  );

  const selectedSessions = useMemo(
    () => Array.from(selectedIds).map((id) => archivedById.get(String(id))).filter(Boolean),
    [selectedIds, archivedById]
  );

  const compareSiteKey = (session) => normalizeArchiveText(
    session?.storeId || session?.siteId || session?.storeName || session?.store || session?.siteName || session?.meta?.storeName || session?.meta?.store,
    "Unknown Site"
  ).toLowerCase();

  const compareSectionKey = (session) => normalizeArchiveText(archiveSectionName(session), "Section").toLowerCase();

  const isCompatiblePair = (first, second) => {
    if (!first || !second) return false;
    if (String(first.sessionId || "") === String(second.sessionId || "")) return true;
    // CI1: comparison compatibility is intentionally simple: same site + same
    // section. Cycle, date, Count Group and targeted-product scope may differ.
    return compareSiteKey(first) === compareSiteKey(second)
      && compareSectionKey(first) === compareSectionKey(second);
  };

  const firstSelectedArchive = selectedSessions[0] || null;

  const archivedCompareCandidates = useMemo(() => {
    if (!firstSelectedArchive) return [];
    const firstId = String(firstSelectedArchive?.sessionId || "");
    const getTime = (item = {}) => {
      const raw = item?.sectionCompletedAt
        || item?.completedAt
        || item?.archivedAt
        || item?.updatedAt
        || item?.startedAt
        || item?.createdAt
        || 0;
      const time = new Date(raw || 0).getTime();
      return Number.isFinite(time) ? time : 0;
    };

    // Archives compare is intentionally archive-only. Active/completed-but-
    // unfinalized Sessions are operational state and must not enter this picker.
    return archived
      .filter(isFinalizedArchiveRecord)
      .filter((candidate) => String(candidate?.sessionId || "") !== firstId)
      .filter((candidate) => isCompatiblePair(firstSelectedArchive, candidate))
      .sort((a, b) => getTime(b) - getTime(a))
      .slice(0, 30);
  }, [firstSelectedArchive, archived]);

  const isSessionCompatible = (candidate) => {
    if (!candidate || !isFinalizedArchiveRecord(candidate)) return false;
    if (selectedIds.has(candidate.sessionId)) return true;
    // Only finalized archives are comparison authorities.
    return selectedSessions.length === 0;
  };

  const toggleSelected = (sessionId) => {
    const candidate = archivedById.get(String(sessionId));
    if (!candidate || !isFinalizedArchiveRecord(candidate)) return;

    setSelectedIds((current) => {
      const ids = Array.from(current);
      if (ids.includes(sessionId)) return new Set();

      // Selecting another archive card starts a new comparison baseline instead
      // of building a second checkbox selection.
      return new Set([sessionId]);
    });

    window.setTimeout(() => {
      const picker = archiveCompareSelectRef.current;
      if (!picker) return;
      picker.focus();
      try {
        if (typeof picker.showPicker === "function") picker.showPicker();
      } catch (_) {}
    }, 220);
  };

  const chooseArchivedCompareSession = (sessionId) => {
    const cleanId = String(sessionId || "").trim();
    if (!cleanId || !firstSelectedArchive) return;

    const candidate = archivedById.get(cleanId);
    if (!candidate || !isFinalizedArchiveRecord(candidate) || !isCompatiblePair(firstSelectedArchive, candidate)) return;

    setSelectedIds(new Set([firstSelectedArchive.sessionId, cleanId]));
  };

  const startComparison = () => {
    if (selectedSessions.length !== 2 || !isCompatiblePair(selectedSessions[0], selectedSessions[1])) return;

    // Comparison identity is chronological, never click order.
    const getTime = (item = {}) => {
      const raw = item?.sectionCompletedAt
        || item?.completedAt
        || item?.archivedAt
        || item?.updatedAt
        || item?.startedAt
        || item?.createdAt
        || 0;
      const time = new Date(raw || 0).getTime();
      return Number.isFinite(time) ? time : 0;
    };
    const chronological = [...selectedSessions].sort((a, b) => getTime(a) - getTime(b));

    navigate(`/morpheus-count/archive?compare=1&ids=${encodeURIComponent(chronological.map((session) => session.sessionId).join(","))}&source=archive`);
  };

  const archiveHierarchy = useMemo(() => buildArchiveHierarchy(filteredArchived), [filteredArchived]);

  useEffect(() => {
    if (compareMode || restoredArchiveScrollRef.current || !archiveHierarchy.length) return;
    const saved = archiveReturnState;
    if (!saved) {
      restoredArchiveScrollRef.current = true;
      window.scrollTo({ top: 0, behavior: "auto" });
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "auto" }));
      return;
    }
    restoredArchiveScrollRef.current = true;
    let attempts = 0;
    const restore = () => {
      const dateTarget = saved.dateKey ? document.querySelector(`[data-archive-date-key="${CSS.escape(saved.dateKey)}"]`) : null;
      const sessionTarget = saved.sessionId ? document.querySelector(`[data-archive-session-id="${CSS.escape(saved.sessionId)}"]`) : null;
      const target = dateTarget || sessionTarget;
      if (target) {
        const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - 110);
        window.scrollTo({ top, behavior: "auto" });
      } else {
        window.scrollTo({ top: saved.y, behavior: "auto" });
      }
      attempts += 1;
      if (attempts < 12) {
        window.requestAnimationFrame(restore);
      }
    };
    window.requestAnimationFrame(restore);
  }, [archiveHierarchy.length, compareMode, archiveReturnState]);
  const finalizedArchived = useMemo(
    () => filteredArchived.filter(isFinalizedArchiveRecord),
    [filteredArchived]
  );

  const finalizedCycleCount = useMemo(() => {
    const uniqueCycles = new Set();
    finalizedArchived.forEach((session) => {
      const dateKey = archiveDateKey(session);
      const siteKey = normalizeArchiveText(session.storeId || session.siteId || session.storeName || session.store || session.siteName || session.meta?.storeName || session.meta?.store, "Unknown Site");
      const cycleKey = archiveCycleId(session);
      uniqueCycles.add(`${dateKey}::${siteKey}::${cycleKey}`);
    });
    return uniqueCycles.size;
  }, [finalizedArchived]);

  const currentExposureSnapshot = useMemo(() => {
    const finalized = finalizedArchived;
    if (!finalized.length) return { exposure: 0, siteCount: 0, dateKey: "" };

    const periodFilterActive = Boolean(
      appliedArchiveFilters.startDate
      || appliedArchiveFilters.endDate
      || appliedArchiveFilters.date
    );

    // Without a period filter, Current Exposure represents the latest archive day.
    // With a period filter, each site's latest fully completed cycle inside that
    // selected period becomes its current verified position.
    let latestDateKey = "";
    let eligible = finalized;
    if (!periodFilterActive) {
      latestDateKey = finalized.reduce((latest, session) => {
        const key = archiveDateKey(session);
        if (!key || key === "Unknown date") return latest;
        return !latest || key > latest ? key : latest;
      }, "");
      if (latestDateKey) eligible = finalized.filter((session) => archiveDateKey(session) === latestDateKey);
    }

    const cycleMap = new Map();
    eligible.forEach((session) => {
      const siteName = normalizeArchiveText(
        session.storeName || session.store || session.siteName || session.meta?.storeName || session.meta?.store,
        "Unknown Site"
      );
      const siteKey = normalizeArchiveText(session.storeId || session.siteId || siteName, siteName);
      const dateKey = archiveDateKey(session);
      const cycleId = archiveCycleId(session);
      const key = `${siteKey}::${dateKey}::${cycleId}`;
      const verifiedAt = archiveTimestampValue(
        session.completedAt
        || session.archivedAt
        || session.updatedAt
        || session.meta?.completedAt
        || session.meta?.archivedAt
        || session.createdAt
      );
      const verifiedTime = verifiedAt ? verifiedAt.getTime() : 0;
      const existing = cycleMap.get(key) || {
        siteKey,
        sessions: [],
        verifiedTime: 0,
      };
      existing.sessions.push(session);
      existing.verifiedTime = Math.max(existing.verifiedTime, verifiedTime);
      cycleMap.set(key, existing);
    });

    const completedCycles = [];
    cycleMap.forEach((cycle) => {
      if (!cycle.sessions.length || !cycle.sessions.every(isCompletedArchiveRecord)) return;

      const fullCountSessions = cycle.sessions.filter((session) => archiveSectionName(session) === "Full Count");
      let exposure = 0;

      if (fullCountSessions.length) {
        // A Full Count already represents the whole site's stock position, so do not
        // add section totals on top of it. Use the latest Full Count in that cycle.
        const latestFullCount = fullCountSessions.reduce((latest, session) => {
          const sessionTime = archiveTimestampValue(
            session.completedAt || session.archivedAt || session.updatedAt || session.createdAt
          )?.getTime() || 0;
          const latestTime = archiveTimestampValue(
            latest?.completedAt || latest?.archivedAt || latest?.updatedAt || latest?.createdAt
          )?.getTime() || 0;
          return !latest || sessionTime >= latestTime ? session : latest;
        }, null);
        exposure = archivedExposureValue(latestFullCount);
      } else {
        // Section-based cycles are one verified stock position. Combine every
        // completed section in that cycle (e.g. Cashier + Storeroom).
        exposure = cycle.sessions.reduce(
          (sum, session) => sum + Number(archivedExposureValue(session) || 0),
          0
        );
      }

      completedCycles.push({
        siteKey: cycle.siteKey,
        exposure: roundTo2(exposure || 0),
        verifiedTime: cycle.verifiedTime,
      });
    });

    const latestCycleBySite = new Map();
    completedCycles.forEach((cycle) => {
      const current = latestCycleBySite.get(cycle.siteKey);
      if (!current || cycle.verifiedTime >= current.verifiedTime) {
        latestCycleBySite.set(cycle.siteKey, cycle);
      }
    });

    const exposure = roundTo2(
      Array.from(latestCycleBySite.values()).reduce((sum, cycle) => sum + Number(cycle.exposure || 0), 0)
    );

    return {
      exposure: exposure || 0,
      siteCount: latestCycleBySite.size,
      dateKey: latestDateKey,
    };
  }, [finalizedArchived, appliedArchiveFilters]);
  const currentExposure = currentExposureSnapshot.exposure;
  const totalFlags = useMemo(
    () => finalizedArchived.reduce((sum, item) => sum + archivedFlagValue(item), 0),
    [finalizedArchived]
  );

  const profileName = String(
    currentProfile?.fullName || currentProfile?.name || currentProfile?.displayName || currentProfile?.email || "Administrator"
  ).trim();

  const activeOperationalKey = (session) => [
    archiveDateKey(session),
    compareSiteKey(session),
    archiveCycleId(session),
    compareSectionKey(session),
  ].join("::");

  const findRestoreConflict = (target) => {
    if (!target) return null;
    const targetKey = activeOperationalKey(target);
    return loadSessionRecords().find((session) => {
      const status = String(session?.status || session?.sessionStatus || "").toLowerCase();
      const genuinelyActive = session && session.archived !== true && status !== "archived" && !session.archivedAt;
      return genuinelyActive && activeOperationalKey(session) === targetKey;
    }) || null;
  };

  const openRestoreFlow = (session) => {
    const conflict = findRestoreConflict(session);
    setRestoreTarget(session);
    setRestoreConflict(conflict);
    setRestoreReason("");
    setRestoreOtherReason("");
  };

  const restoreReasonValid = Boolean(restoreReason && (restoreReason !== "Other" || restoreOtherReason.trim()));

  const openExistingSession = () => {
    if (!restoreConflict?.sessionId) return;
    setRestoreTarget(null);
    setRestoreConflict(null);
    navigate(`/morpheus-count/session/${restoreConflict.sessionId}`);
  };

  const confirmRestore = async (mode = "new") => {
    if (!restoreTarget || !canRestore || restoreBusy || !restoreReasonValid) return;
    setRestoreBusy(true);
    try {
      const restoredSessionId = restoreTarget.sessionId;
      const resolvedReason = restoreReason === "Other" ? restoreOtherReason.trim() : restoreReason;
      const restoreResult = await restoreArchivedSessionRecord(restoredSessionId, {
        mode,
        replaceSessionId: mode === "replace" ? restoreConflict?.sessionId : null,
        restoreReason: resolvedReason,
        restoreRequestedBy: profileName,
        restoreRequestedByProfileId: currentProfile?.id || currentProfile?.profileId || null,
      });
      if (restoreResult?.ok === false) {
        setRestoreConflict(restoreResult.conflict || findRestoreConflict(restoreTarget));
        setRestoreResult({ error: true, message: restoreResult.reason || "Restore could not be completed safely." });
        return;
      }
      setRestoreTarget(null);
      setRestoreConflict(null);
      setRestoreReason("");
      setRestoreOtherReason("");
      setRefreshKey((v) => v + 1);
      setRestoreResult({
        sessionId: restoredSessionId,
        mode,
        reason: resolvedReason,
        site: restoreTarget.storeName || restoreTarget.store || restoreTarget.siteName || "Site",
        section: archiveSectionName(restoreTarget),
      });
      window.setTimeout(() => {
        navigate(`/morpheus-count/session/${restoredSessionId}?restored=1`, {
          state: {
            restoredFromArchive: true,
            restoreReason: resolvedReason,
            restoreMode: mode,
            restoredBy: profileName,
          },
        });
      }, 1100);
    } finally {
      setRestoreBusy(false);
    }
  };


  if (compareMode) {
    return (
      <div className="mc-archive-page mc-archive-page--compare mc-tabPage">
        <div className="mc-archive-bg" />
        <McPageHeader activeKey="archive" />
        {compareSessions.length >= 2 ? (
          <SessionComparisonPage sessions={compareSessions} onBack={() => navigate(compareBackPath)} backLabel={compareBackLabel} />
        ) : (
          <div className="mc-compare-empty">
            <div className="mc-archive-label">Session comparison</div>
            <h1>Choose two sessions</h1>
            <p>Go back to Archives and select exactly two sessions to compare.</p>
            <button className="mc-archive-btn mc-archive-btn--primary" type="button" onClick={() => navigate(compareBackPath)}>{compareBackLabel}</button>
          </div>
        )}
        <McPageFooter />
      </div>
    );
  }

  return (
    <div className="mc-archive-page mc-tabPage">
      <div className="mc-archive-bg" />

      <McPageHeader activeKey="archive" />

      <div className="mc-archive-topbar">
        <div className="mc-archive-card"><span>Finalized Cycles</span><strong>{finalizedCycleCount}</strong></div>
        <div className="mc-archive-card"><span>Finalized Sections</span><strong>{finalizedArchived.length}</strong></div>
        <div className="mc-archive-card mc-archive-card--exposure">
          <span>Latest Finalized Exposure</span>
          <strong className={`mc-archive-totalExposure ${currentExposure < 0 ? "is-negative" : currentExposure > 0 ? "is-positive" : ""}`}>{formatExposure(currentExposure)}</strong>
          <small className={`mc-archive-card__support ${currentExposure < 0 ? "is-negative" : currentExposure > 0 ? "is-positive" : ""}`}>
            Latest finalized stock position across selected sites
          </small>
        </div>
        <div className="mc-archive-card"><span>Finalized Flags</span><strong>{totalFlags}</strong></div>
      </div>

      <form className="mc-archive-filters" onSubmit={applyArchiveFilters}>
        <label>
          <span>From date</span>
          <input
            type="date"
            value={archiveFilters.startDate}
            onChange={(event) => setArchiveFilters((current) => ({ ...current, startDate: event.target.value }))}
          />
        </label>
        <label>
          <span>To date</span>
          <input
            type="date"
            min={archiveFilters.startDate || undefined}
            value={archiveFilters.endDate}
            onChange={(event) => setArchiveFilters((current) => ({ ...current, endDate: event.target.value }))}
          />
        </label>
        <label>
          <span>User name</span>
          <input
            type="text"
            list="mc-archive-user-options"
            placeholder="Search and select user"
            value={archiveFilters.name}
            onChange={(event) => setArchiveFilters((current) => ({ ...current, name: event.target.value }))}
          />
          <datalist id="mc-archive-user-options">
            {archiveUserOptions.map((name) => <option key={name} value={name} />)}
          </datalist>
        </label>
        <label>
          <span>Section</span>
          <select
            value={archiveFilters.section}
            onChange={(event) => setArchiveFilters((current) => ({ ...current, section: event.target.value }))}
          >
            <option value="">All Sections</option>
            {archiveSectionOptions.map((section) => <option key={section} value={section}>{section}</option>)}
          </select>
        </label>
        <div className="mc-archive-filterActions">
          <button className="mc-archive-btn mc-archive-btn--primary" type="submit">Search</button>
          <button className="mc-archive-btn mc-archive-btn--quiet" type="button" onClick={clearArchiveFilters}>Clear</button>
        </div>
      </form>

      <ArchiveV2
        hierarchy={archiveHierarchy}
        canRestore={canRestore}
        returnState={archiveReturnState}
        onRestore={openRestoreFlow}
        selectedIds={selectedIds}
        onToggleSelected={toggleSelected}
        isSessionCompatible={isSessionCompatible}
      />

      {selectedIds.size ? (
        <aside className="mc-archive-compareTray" aria-live="polite">
          <div className="mc-archive-compareTray__title">
            <span>Compare archived counts</span>
            <strong>{selectedSessions.length === 2 ? "Ready to compare" : "1 count selected — choose its matching archive"}</strong>
          </div>

          <div className="mc-archive-compareTray__slots">
            <div className="mc-archive-compareSlot is-filled">
              <span>A</span>
              <div>
                <strong>{firstSelectedArchive ? archiveSectionName(firstSelectedArchive) : "First count"}</strong>
                <small>
                  {firstSelectedArchive
                    ? `${firstSelectedArchive.storeName || firstSelectedArchive.store || firstSelectedArchive.siteName || "Site"} • ${archiveDateLabel(archiveDateKey(firstSelectedArchive))} • ${firstSelectedArchive.cycleName || firstSelectedArchive.countCycleName || "Cycle"}`
                    : "Select an archived count"}
                </small>
              </div>
            </div>

            <div className={`mc-archive-compareSlot ${selectedSessions[1] ? "is-filled" : ""}`}>
              <span>B</span>
              <div style={{ width: "100%" }}>
                {firstSelectedArchive ? (
                  <>
                    <strong>{selectedSessions[1] ? archiveSectionName(selectedSessions[1]) : `Compare with another archived ${archiveSectionName(firstSelectedArchive)}`}</strong>
                    <select
                      ref={archiveCompareSelectRef}
                      value={selectedSessions[1]?.sessionId || ""}
                      onChange={(event) => chooseArchivedCompareSession(event.target.value)}
                      aria-label="Choose another archived count"
                      style={{ width: "100%", marginTop: 8 }}
                    >
                      <option value="">
                        {archivedCompareCandidates.length
                          ? "Choose another finalized count — any date or cycle"
                          : "No other comparable finalized counts found"}
                      </option>
                      {archivedCompareCandidates.map((candidate) => (
                        <option key={candidate.sessionId} value={candidate.sessionId}>
                          {`${archiveDateLabel(archiveDateKey(candidate))} • ${candidate.cycleName || candidate.countCycleName || "Cycle"} • ${candidate.storeName || candidate.store || candidate.siteName || "Site"}`}
                        </option>
                      ))}
                    </select>
                    <small>
                      Finalized archives only • Same site + same section • Any finalized date or cycle
                    </small>
                  </>
                ) : (
                  <>
                    <strong>Select another matching archive</strong>
                    <small>Waiting for first selection</small>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mc-archive-compareTray__actions">
            <button
              className="mc-archive-btn mc-archive-btn--quiet"
              type="button"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </button>
            <button
              className="mc-archive-btn mc-archive-btn--primary"
              type="button"
              onClick={startComparison}
              disabled={selectedSessions.length !== 2}
            >
              Compare Counts
            </button>
          </div>
        </aside>
      ) : null}

      {!filtersActive ? (
        <div className="mc-archive-monthLimit">
          <span>{showOlderArchives ? "Showing all archived dates." : "Showing this month by default."}</span>
          <button
            className="mc-archive-btn mc-archive-btn--quiet mc-archive-btn--small"
            type="button"
            onClick={() => setShowOlderArchives((value) => !value)}
          >
            {showOlderArchives ? "Show this month only" : "Show previous months"}
          </button>
        </div>
      ) : null}



      {restoreResult ? (
        <div className="mc-archive-restoreSuccess" role="status" aria-live="assertive">
          <div className="mc-archive-restoreSuccess__spinner" aria-hidden="true" />
          <div>
            <span>Session restored</span>
            <h2>{restoreResult.site} · {restoreResult.section}</h2>
            <p>{restoreResult.mode === "replace" ? "Archived count replaced the existing active version." : "Archived count returned to Active Sessions."}</p>
            <small>Preparing the live count workspace…</small>
          </div>
        </div>
      ) : null}

      {restoreTarget ? (
        <ConfirmModal
          label={restoreConflict ? "Restore decision" : "Restore archived session"}
          title={restoreConflict
            ? (String(restoreConflict.status || "").toLowerCase() === "finalized" ? "Restore unavailable" : "Active session found")
            : `Restore ${restoreTarget.sessionId}?`}
          body={restoreConflict
            ? (String(restoreConflict.status || "").toLowerCase() === "finalized"
              ? "A finalized session already exists for this site, cycle, section and operational date. Archive or remove it before restoring another version."
              : `A ${String(restoreConflict.status || "active").toLowerCase()} session already exists for this site, cycle, section and operational date.`)
            : "This archived count will be restored into Active Sessions."}
          confirmText={restoreConflict ? "Replace With Archived" : "Restore Session"}
          confirmDisabled={!restoreReasonValid}
          confirmDisabledText="Select reason to restore"
          danger={Boolean(restoreConflict)}
          busy={restoreBusy}
          onCancel={() => {
            if (!restoreBusy) {
              setRestoreTarget(null);
              setRestoreConflict(null);
              setRestoreReason("");
              setRestoreOtherReason("");
            }
          }}
          secondaryAction={restoreConflict && String(restoreConflict.status || "").toLowerCase() !== "finalized"
            ? { label: String(restoreConflict.status || "").toLowerCase() === "completed" ? "Open Current" : "Continue Live Count", onClick: openExistingSession }
            : null}
          onConfirm={restoreConflict && String(restoreConflict.status || "").toLowerCase() === "finalized"
            ? null
            : () => confirmRestore(restoreConflict ? "replace" : "new")}
        >
          <div className="mc-archive-restoreSummary">
            <div><span>Site</span><strong>{restoreTarget.storeName || restoreTarget.store || restoreTarget.siteName || "Unknown Site"}</strong></div>
            <div><span>Cycle</span><strong>{archiveCycleName(restoreTarget)}</strong></div>
            <div><span>Section</span><strong>{archiveSectionName(restoreTarget)}</strong></div>
            <div><span>Operational date</span><strong>{archiveDateLabel(archiveDateKey(restoreTarget))}</strong></div>
            <div><span>Exposure</span><strong>{formatCurrency(archivedExposureValue(restoreTarget))}</strong></div>
            <div><span>Flags</span><strong>{archivedFlagValue(restoreTarget)}</strong></div>
          </div>
          {(!restoreConflict || String(restoreConflict.status || "").toLowerCase() !== "finalized") ? (
            <div className="mc-archive-restoreForm">
              <label>
                <span>Restore reason</span>
                <select value={restoreReason} onChange={(event) => setRestoreReason(event.target.value)} disabled={restoreBusy}>
                  <option value="">Select a reason</option>
                  <option value="Recount Requested">Recount Requested</option>
                  <option value="Data Correction">Data Correction</option>
                  <option value="Training">Training</option>
                  <option value="Investigation">Investigation</option>
                  <option value="Other">Other</option>
                </select>
              </label>
              {restoreReason === "Other" ? (
                <label>
                  <span>Reason details</span>
                  <textarea value={restoreOtherReason} onChange={(event) => setRestoreOtherReason(event.target.value)} placeholder="Explain why this count is being restored" rows={3} disabled={restoreBusy} />
                </label>
              ) : null}
              {!restoreReasonValid && restoreReason ? <small className="mc-archive-restoreForm__error">Please provide the reason details.</small> : null}
            </div>
          ) : null}
        </ConfirmModal>
      ) : null}

      <McPageFooter />
    </div>
  );
}
